import resizeWasmUrl from '@jsquash/resize/lib/resize/pkg/squoosh_resize_bg.wasm';
import {
  type DragEvent as ReactDragEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';

import { getCsrfToken } from '../../utils/csrf';
import Icon, { type IconName } from '../Icon';
import {
  getImageUploaderCacheRecord,
  type ImageUploaderAssetAttribution,
  type ImageUploaderCacheManifestRecord,
  type ImageUploaderCacheRecordInput,
  type ImageUploaderEditState,
  type ImageUploaderPersistedAssetRef,
  listImageUploaderCacheManifests,
  saveImageUploaderCacheRecord,
  touchImageUploaderCacheRecord,
  updateImageUploaderCachePersistedAsset,
} from './image_uploader_cache';

type SourceType = 'upload' | 'link' | 'unsplash' | 'color';
type ChooserTab = SourceType | 'recent' | 'edit';

type AssetAttribution = ImageUploaderAssetAttribution;

type WebpEncodingMode = 'lossless' | 'lossy';

type VariantScale = 0 | 1 | 2;
type VariantDescriptor = 'editable' | '1x' | '2x';

type ExportedVariant = {
  scale: VariantScale;
  descriptor: VariantDescriptor;
  blob: Blob;
  width: number;
  height: number;
  bytes: number;
  mimeType: string;
};

type CachedVariantSummary = {
  scale: VariantScale;
  descriptor: VariantDescriptor;
  width: number;
  height: number;
  bytes: number;
  mimeType: string;
};

type CachedAssetSummary = {
  id: string;
  source: SourceType;
  previewUrl: string;
  persistedIdentity?: string;
  width: number;
  height: number;
  bytes: number;
  mimeType: string;
  variants: CachedVariantSummary[];
  createdAt: string;
  totalBytes: number;
  attribution?: AssetAttribution;
};

type UnsplashPhoto = {
  id: string;
  thumbUrl: string;
  fullUrl: string;
  author: string;
  authorUrl: string;
};

type StoredDefaultGallerySearch = {
  query: string;
  page: number;
  hasMore: boolean;
  storedAt: string;
  results: UnsplashPhoto[];
};

type CropRect = {
  x: number;
  y: number;
  width: number;
  height: number;
};

type ImageEditState = ImageUploaderEditState;

type CropDragMode = 'move' | 'resize-top-left' | 'resize-top-right' | 'resize-bottom-left' | 'resize-bottom-right';

type CropDragState = {
  mode: CropDragMode;
  pointerId: number;
  startX: number;
  startY: number;
  initialCrop: CropRect;
};

type ImageUploaderProps = {
  unsplashAccessKey?: string;
  unsplashPerPage?: number;
  cacheKey?: string;
  maxCachedItems?: number;
  defaultTab?: SourceType;
  config?: ImageUploaderConfigInput;
  closeModalId?: string;
  eventName?: string;
  target?: string;
  uploadUrl: string;
  // Standalone screens (for example Edit Logo / Edit bg) can pass the already
  // persisted backend asset here without going through the regular field value flow.
  initialAsset?: string | PersistedAsset | null;
  // Regular field flow uses the saved field value as the persisted asset source.
  persistedValue?: string | PersistedAsset | null;
  appliedImageSelector?: string;
  pushEventTo?: (target: HTMLElement, event: string, payload: Record<string, unknown>) => void;
};

type ImageUploaderPresetInput = {
  key?: string;
  label?: string;
  width?: number | string;
  height?: number | string;
  // Keeps the source image's aspect ratio; `height` is derived from the image.
  original?: boolean | string;
};

type ImageUploaderPreset = {
  key: string;
  label: string;
  width: number;
  height: number;
  original: boolean;
};

type DisplaySize = 'small' | 'medium' | 'large' | 'full';
type DisplayAlign = 'left' | 'center' | 'right';
type DisplaySettings = { size: DisplaySize; align: DisplayAlign };

type ImageUploaderConfigInput = {
  fixed_size?: boolean | string;
  target_width?: number | string;
  target_height?: number | string;
  crop_ratio_width?: number | string;
  crop_ratio_height?: number | string;
  presets?: ImageUploaderPresetInput[];
  sources?: Array<SourceType | string>;
  size_inputs?: boolean | string;
  display_options?: boolean | string;
};

type ImageUploaderConfig = {
  fixedSize: boolean;
  targetWidth: number;
  targetHeight: number;
  cropRatioWidth?: number;
  cropRatioHeight?: number;
  presets: ImageUploaderPreset[];
  sources: SourceType[];
  showSizeInputs: boolean;
  showDisplayOptions: boolean;
  defaultsToOriginalAspect: boolean;
};

type PersistedVariant = CachedVariantSummary & {
  url?: string;
  filename?: string;
  imageUploadId?: string;
};

type PersistedAsset = {
  source: SourceType;
  url?: string;
  filename?: string;
  imageUploadId?: string;
  width: number;
  height: number;
  bytes: number;
  mimeType: string;
  createdAt: string;
  edit: ImageEditState;
  attribution?: AssetAttribution;
  variants: PersistedVariant[];
  display?: DisplaySettings;
  alt?: string;
};

type WorkingImage = {
  dataUrl: string;
  source: SourceType;
  naturalWidth: number;
  naturalHeight: number;
  cacheAssetId?: string;
  persistedIdentity?: string;
  attribution?: AssetAttribution;
};

type AppliedEditorState = {
  imageIdentity: string;
  crop: CropRect;
  targetWidth: number;
  targetHeight: number;
};

type PendingAutoApply = {
  dataUrl: string;
  closeHost: boolean;
};

type PreviewAsset = {
  src: string;
  blob: Blob;
  bytes: number;
  mimeType: string;
};

type ImageExportWorkerRequest = {
  sourceDataUrl: string;
  crop: CropRect;
  targetWidth: number;
  targetHeight: number;
  scales: Array<Exclude<VariantScale, 0>>;
  includeEditable?: boolean;
  encodingMode: WebpEncodingMode;
};

type ImageExportWorkerSuccessMessage = {
  type: 'success';
  variants: ExportedVariant[];
};

type ImageExportWorkerErrorMessage = {
  type: 'error';
  message: string;
};

type ResizeModule = typeof import('@jsquash/resize');

const DEFAULT_CACHE_KEY = 'image-uploader-cache';
const DEFAULT_MAX_CACHE_ITEMS = 12;
const DEFAULT_MAX_CACHE_TOTAL_BYTES = 96 * 1024 * 1024;
const DEFAULT_UNSPLASH_PER_PAGE = 20;
const DEFAULT_TARGET_SIZE = { width: 1200, height: 630 };
const LOSSY_WEBP_QUALITY = 84;
const LOSSY_WEBP_ALPHA_QUALITY = 90;
const RESPONSIVE_VARIANT_SCALES = [1, 2] as const;
const PREVIEW_REBUILD_DELAY_MS = 120;
const CROP_AUTO_SAVE_DELAY_MS = 500;
const EXPORT_LONG_EDGE = 1600;
const MAX_PREVIEW_LONG_EDGE = 1400;
const FLUXON_OUTLINE_BUTTON_BASE_CLASS =
  'inline-flex items-center justify-center bg-white text-base-900 shadow-xs ring-1 ring-inset ring-base-200 transition hover:bg-base-50 focus-visible:outline-2 focus-visible:outline-indigo-600 focus-visible:outline-offset-2';
const FLUXON_OUTLINE_BUTTON_SM_CLASS = `${FLUXON_OUTLINE_BUTTON_BASE_CLASS} rounded-md px-3 py-2 text-sm font-semibold`;
const FLUXON_SOLID_BUTTON_CLASS =
  'inline-flex items-center justify-center rounded-md bg-indigo-600 px-4 py-3 text-sm font-semibold text-white shadow-xs transition hover:bg-indigo-500 focus-visible:outline-2 focus-visible:outline-indigo-600 focus-visible:outline-offset-2';

let resizeModulePromise: Promise<ResizeModule> | null = null;

function resolveAssetUrl(assetPath: string): string {
  if (/^(https?:|data:|blob:)/.test(assetPath)) {
    return assetPath;
  }

  if (assetPath.startsWith('/')) {
    return assetPath;
  }

  const baseUrl =
    typeof window === 'undefined' ? 'http://localhost/assets/app.js' : `${window.location.origin}/assets/app.js`;

  return new URL(assetPath, baseUrl).toString();
}

async function exportProcessedVariantsInWorker(request: ImageExportWorkerRequest): Promise<ExportedVariant[]> {
  if (typeof Worker === 'undefined' || typeof window === 'undefined') {
    throw new Error('Web Worker export is unavailable');
  }

  const workerUrl = new URL('/assets/image_uploader_export_worker.js', window.location.origin).toString();

  return new Promise((resolve, reject) => {
    const worker = new Worker(workerUrl, { type: 'module', name: 'image-uploader-export' });

    const cleanup = () => {
      worker.onmessage = null;
      worker.onerror = null;
      worker.onmessageerror = null;
      worker.terminate();
    };

    worker.onmessage = event => {
      const message = event.data as ImageExportWorkerSuccessMessage | ImageExportWorkerErrorMessage;
      cleanup();

      if (message?.type === 'success') {
        resolve(message.variants);
        return;
      }

      reject(new Error(message?.type === 'error' ? message.message : 'Image export worker failed'));
    };

    worker.onerror = event => {
      cleanup();
      reject(new Error(event.message || 'Image export worker crashed'));
    };

    worker.onmessageerror = () => {
      cleanup();
      reject(new Error('Image export worker message failed'));
    };

    worker.postMessage(request);
  });
}

async function getResizeModule(): Promise<ResizeModule> {
  if (!resizeModulePromise) {
    resizeModulePromise = import('@jsquash/resize')
      .then(async module => {
        await module.initResize(resolveAssetUrl(resizeWasmUrl as unknown as string));
        return module;
      })
      .catch(error => {
        resizeModulePromise = null;
        throw error;
      });
  }

  return resizeModulePromise;
}

const tabLabel: Record<SourceType, string> = {
  upload: 'Upload',
  link: 'Link',
  unsplash: 'Gallery',
  color: 'Color',
};

const defaultSourceTabs: SourceType[] = ['unsplash', 'upload', 'link', 'color'];

const defaultOutputSizePresets: readonly ImageUploaderPreset[] = [
  { key: 'social', label: 'Social', width: 1200, height: 630, original: false },
  { key: 'square', label: 'Square', width: 1080, height: 1080, original: false },
  { key: 'story', label: 'Story', width: 1080, height: 1920, original: false },
  { key: 'banner', label: 'Banner', width: 1600, height: 900, original: false },
];

const FULL_CROP: CropRect = { x: 0, y: 0, width: 100, height: 100 };

const DEFAULT_DISPLAY_SETTINGS: DisplaySettings = { size: 'medium', align: 'center' };
const DISPLAY_SIZE_OPTIONS: { value: DisplaySize; label: string; description: string }[] = [
  { value: 'small', label: 'Small', description: 'Up to 256px wide' },
  { value: 'medium', label: 'Medium', description: 'Up to 400px wide' },
  { value: 'large', label: 'Large', description: 'Up to 600px wide' },
  { value: 'full', label: 'Full', description: 'The full width' },
];
const DISPLAY_ALIGN_OPTIONS: { value: DisplayAlign; label: string; icon: IconName }[] = [
  { value: 'left', label: 'Left', icon: 'untitled-align-left' },
  { value: 'center', label: 'Center', icon: 'untitled-align-center' },
  { value: 'right', label: 'Right', icon: 'untitled-align-right' },
];
const ASPECT_LOCK_OPTIONS: { value: 'locked' | 'unlocked'; label: string; icon: IconName }[] = [
  { value: 'locked', label: 'Locked', icon: 'untitled-lock-01' },
  { value: 'unlocked', label: 'Unlocked', icon: 'untitled-lock-unlocked-01' },
];

const COLOR_PRESETS = ['#1F2937', '#4338CA', '#C4B5FD', '#F2D0B7', '#111827', '#0EA5E9'];
const DEFAULT_SELECTED_COLOR = COLOR_PRESETS[0] ?? '#1F2937';
const MAX_CUSTOM_COLOR_SWATCHES = 12;
const CUSTOM_COLOR_STORAGE_KEY_PREFIX = 'image-uploader-custom-colors';
const DEFAULT_GALLERY_QUERY = 'nature';
const DEFAULT_GALLERY_SEARCH_STORAGE_KEY_PREFIX = 'image-uploader-default-gallery-search';
const DEFAULT_GALLERY_SEARCH_CACHE_TTL_MS = 6 * 60 * 60 * 1000;

function normalizeHexColorValue(color: string): string | null {
  const normalized = color.trim().toUpperCase();
  return /^#[0-9A-F]{6}$/.test(normalized) ? normalized : null;
}

function getCustomColorStorageKey(cacheKey: string): string {
  return `${CUSTOM_COLOR_STORAGE_KEY_PREFIX}:${cacheKey}`;
}

function readStoredCustomColors(cacheKey: string): string[] {
  if (typeof window === 'undefined') {
    return [];
  }

  try {
    const raw = window.localStorage?.getItem(getCustomColorStorageKey(cacheKey));
    if (!raw) {
      return [];
    }

    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return [];
    }

    return parsed
      .map(value => (typeof value === 'string' ? normalizeHexColorValue(value) : null))
      .filter((value): value is string => value !== null)
      .filter(color => !COLOR_PRESETS.some(preset => preset.toUpperCase() === color))
      .slice(0, MAX_CUSTOM_COLOR_SWATCHES);
  } catch {
    return [];
  }
}

function writeStoredCustomColors(cacheKey: string, colors: string[]): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage?.setItem(getCustomColorStorageKey(cacheKey), JSON.stringify(colors));
  } catch {
    // Ignore storage errors and keep the in-memory palette usable.
  }
}

function getDefaultGallerySearchStorageKey(cacheKey: string): string {
  return `${DEFAULT_GALLERY_SEARCH_STORAGE_KEY_PREFIX}:${cacheKey}`;
}

function readStoredDefaultGallerySearch(cacheKey: string): StoredDefaultGallerySearch | null {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const raw = window.localStorage?.getItem(getDefaultGallerySearchStorageKey(cacheKey));
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as Partial<StoredDefaultGallerySearch> | null;
    if (!parsed || typeof parsed !== 'object' || !Array.isArray(parsed.results)) {
      return null;
    }

    const results = parsed.results
      .map(item => {
        if (
          !item ||
          typeof item !== 'object' ||
          typeof item.id !== 'string' ||
          typeof item.thumbUrl !== 'string' ||
          typeof item.fullUrl !== 'string' ||
          typeof item.author !== 'string' ||
          typeof item.authorUrl !== 'string'
        ) {
          return null;
        }

        return item satisfies UnsplashPhoto;
      })
      .filter((item): item is UnsplashPhoto => item !== null);

    if (results.length === 0) {
      return null;
    }

    return {
      query: typeof parsed.query === 'string' && parsed.query.trim() ? parsed.query.trim() : DEFAULT_GALLERY_QUERY,
      page: typeof parsed.page === 'number' && Number.isFinite(parsed.page) && parsed.page > 0 ? parsed.page : 1,
      hasMore: parsed.hasMore === true,
      storedAt: typeof parsed.storedAt === 'string' ? parsed.storedAt : new Date(0).toISOString(),
      results,
    };
  } catch {
    return null;
  }
}

function writeStoredDefaultGallerySearch(
  cacheKey: string,
  payload: Pick<StoredDefaultGallerySearch, 'query' | 'page' | 'hasMore' | 'results'>,
): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage?.setItem(
      getDefaultGallerySearchStorageKey(cacheKey),
      JSON.stringify({
        ...payload,
        storedAt: new Date().toISOString(),
      } satisfies StoredDefaultGallerySearch),
    );
  } catch {
    // Ignore storage errors and keep gallery search usable.
  }
}

function isStoredDefaultGallerySearchFresh(entry: StoredDefaultGallerySearch | null): boolean {
  if (!entry) {
    return false;
  }

  const storedAt = Date.parse(entry.storedAt);
  if (!Number.isFinite(storedAt)) {
    return false;
  }

  return Date.now() - storedAt < DEFAULT_GALLERY_SEARCH_CACHE_TTL_MS;
}

function areUnsplashResultsEqual(left: UnsplashPhoto[], right: UnsplashPhoto[]): boolean {
  return (
    left.length === right.length &&
    left.every((item, index) => {
      const candidate = right[index];
      return (
        candidate &&
        item.id === candidate.id &&
        item.thumbUrl === candidate.thumbUrl &&
        item.fullUrl === candidate.fullUrl &&
        item.author === candidate.author &&
        item.authorUrl === candidate.authorUrl
      );
    })
  );
}

function toPositiveNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
    return Math.floor(value);
  }

  if (typeof value === 'string') {
    const parsed = Number(value);
    if (Number.isFinite(parsed) && parsed > 0) {
      return Math.floor(parsed);
    }
  }

  return null;
}

function toBoolean(value: unknown): boolean {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') return value === 'true';
  return false;
}

function normalizePresetLabel(label: string | undefined, width: number, height: number): string {
  const trimmedLabel = label?.trim();
  if (trimmedLabel) return trimmedLabel;

  return `${width} x ${height}`;
}

function normalizePresetKey(key: string | undefined, label: string, width: number, height: number): string {
  const trimmedKey = key?.trim();
  if (trimmedKey) return trimmedKey;

  return `${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-${width}x${height}`;
}

function normalizeConfiguredPresets(presets: ImageUploaderPresetInput[] | undefined): ImageUploaderPreset[] {
  if (!Array.isArray(presets) || presets.length === 0) {
    return [...defaultOutputSizePresets];
  }

  const normalized = presets
    .map(preset => {
      const original = toBoolean(preset.original);
      const width = toPositiveNumber(preset.width);
      const height = toPositiveNumber(preset.height) ?? (original ? width : null);
      if (!width || !height) return null;

      const label = normalizePresetLabel(preset.label, width, height);

      return {
        key: normalizePresetKey(preset.key, label, width, height),
        label,
        width,
        height,
        original,
      } satisfies ImageUploaderPreset;
    })
    .filter((preset): preset is ImageUploaderPreset => preset !== null);

  return normalized.length > 0 ? normalized : [...defaultOutputSizePresets];
}

function normalizeConfiguredSources(sources: Array<SourceType | string> | undefined): SourceType[] {
  if (!Array.isArray(sources) || sources.length === 0) {
    return [...defaultSourceTabs];
  }

  const normalized = sources.filter((source): source is SourceType => defaultSourceTabs.includes(source as SourceType));

  if (normalized.length === 0) {
    return [...defaultSourceTabs];
  }

  const uniqueSources = Array.from(new Set(normalized));
  return uniqueSources.includes('unsplash')
    ? ['unsplash', ...uniqueSources.filter(source => source !== 'unsplash')]
    : uniqueSources;
}

function resolveDefaultSourceTab(allowedSourceTabs: SourceType[], requestedDefaultTab: SourceType): SourceType {
  if (allowedSourceTabs.includes('unsplash')) {
    return 'unsplash';
  }

  if (allowedSourceTabs.includes(requestedDefaultTab)) {
    return requestedDefaultTab;
  }

  return allowedSourceTabs[0] ?? 'upload';
}

function normalizeImageUploaderConfig(config?: ImageUploaderConfigInput): ImageUploaderConfig {
  const configObject = config ?? {};
  const presets = normalizeConfiguredPresets(configObject.presets);
  const sources = normalizeConfiguredSources(configObject.sources);
  const configuredTargetWidth = toPositiveNumber(configObject.target_width);
  const configuredTargetHeight = toPositiveNumber(configObject.target_height);
  const configuredCropRatioWidth = toPositiveNumber(configObject.crop_ratio_width);
  const configuredCropRatioHeight = toPositiveNumber(configObject.crop_ratio_height);
  const matchingPreset =
    presets.find(
      preset => !preset.original && preset.width === configuredTargetWidth && preset.height === configuredTargetHeight,
    ) ?? null;
  const fallbackPreset: ImageUploaderPreset = presets[0] ?? {
    key: 'default',
    label: 'Default',
    width: DEFAULT_TARGET_SIZE.width,
    height: DEFAULT_TARGET_SIZE.height,
    original: false,
  };
  const targetWidth = configuredTargetWidth ?? matchingPreset?.width ?? fallbackPreset.width;
  const targetHeight = configuredTargetHeight ?? matchingPreset?.height ?? fallbackPreset.height;
  const requestedFixedSize = toBoolean(configObject.fixed_size);
  const onlyPreset = presets.length === 1 ? (presets[0] ?? null) : null;

  return {
    // A lone "original" preset still has to follow each image's aspect, so it can't be fixed.
    fixedSize: requestedFixedSize || (onlyPreset !== null && !onlyPreset.original),
    targetWidth,
    targetHeight,
    ...(configuredCropRatioWidth && configuredCropRatioHeight
      ? {
          cropRatioWidth: configuredCropRatioWidth,
          cropRatioHeight: configuredCropRatioHeight,
        }
      : {}),
    presets,
    sources,
    showSizeInputs: configObject.size_inputs === undefined ? true : toBoolean(configObject.size_inputs),
    showDisplayOptions: toBoolean(configObject.display_options),
    defaultsToOriginalAspect: matchingPreset === null && configuredTargetWidth === null && fallbackPreset.original,
  };
}

function isFullCrop(crop: CropRect): boolean {
  return crop.x === 0 && crop.y === 0 && crop.width === 100 && crop.height === 100;
}

function getOriginalAspectTarget(
  width: number,
  image: Pick<WorkingImage, 'naturalWidth' | 'naturalHeight'>,
): { width: number; height: number } {
  return { width, height: Math.max(1, Math.round((width * image.naturalHeight) / image.naturalWidth)) };
}

function normalizeDisplaySettings(value: unknown): DisplaySettings | null {
  if (!value || typeof value !== 'object') return null;

  const { size, align } = value as { size?: unknown; align?: unknown };
  const validSize = DISPLAY_SIZE_OPTIONS.some(option => option.value === size);
  const validAlign = DISPLAY_ALIGN_OPTIONS.some(option => option.value === align);

  return validSize && validAlign ? { size: size as DisplaySize, align: align as DisplayAlign } : null;
}

function applyDisplayFields(asset: PersistedAsset, display: DisplaySettings | null, alt: string): PersistedAsset {
  const next: PersistedAsset = { ...asset };
  delete next.display;
  delete next.alt;

  if (display) next.display = display;
  const trimmedAlt = alt.trim();
  if (trimmedAlt) next.alt = trimmedAlt;

  return next;
}

const OPTION_LABEL_CLASS = 'text-base-500 mb-1.5 block text-xs font-medium';

function optionChipClass(isActive: boolean): string {
  return `shadow-xs inline-flex items-center justify-center rounded-md px-3 py-2 text-xs font-semibold ring-1 ring-inset transition ${
    isActive ? 'bg-base-100 text-base-900 ring-base-300' : 'text-base-700 ring-base-200 hover:bg-base-50 bg-white'
  }`;
}

// Expands in the document flow rather than floating: a floating menu inside the
// editor popover gets clipped by its scroll container and competes with its dismissal.
function DisplayOptionSelect<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: readonly { value: T; label: string; description: string }[];
  onChange: (value: T) => void;
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const listId = useId();
  const selected = options.find(option => option.value === value) ?? options[0];

  useEffect(() => {
    if (!isExpanded) return;

    listRef.current?.querySelector<HTMLButtonElement>('[aria-selected="true"]')?.focus();

    // Fluxon dismisses the popover from a capture listener on document; a window
    // capture listener runs first, so Escape only collapses this list.
    const collapseOnEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      event.stopImmediatePropagation();
      setIsExpanded(false);
      triggerRef.current?.focus();
    };

    window.addEventListener('keydown', collapseOnEscape, true);
    return () => window.removeEventListener('keydown', collapseOnEscape, true);
  }, [isExpanded]);

  if (!selected) return null;

  const moveFocus = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    const step = ({ ArrowDown: 1, ArrowUp: -1 } as Record<string, number>)[event.key];
    if (!step) return;

    event.preventDefault();
    const items = [...(listRef.current?.querySelectorAll<HTMLButtonElement>('[role="option"]') ?? [])];
    const currentIndex = items.findIndex(item => item === document.activeElement);
    items[(currentIndex + step + items.length) % items.length]?.focus();
  };

  const choose = (nextValue: T) => {
    setIsExpanded(false);
    triggerRef.current?.focus();
    if (nextValue !== value) onChange(nextValue);
  };

  return (
    <div
      ref={rootRef}
      className='min-w-0'
      onBlur={event => {
        if (!rootRef.current?.contains(event.relatedTarget as Node | null)) setIsExpanded(false);
      }}
    >
      <p className={OPTION_LABEL_CLASS}>{label}</p>
      <button
        ref={triggerRef}
        type='button'
        aria-label={`${label}: ${selected.label}`}
        aria-expanded={isExpanded}
        aria-controls={listId}
        onClick={() => setIsExpanded(current => !current)}
        className='ring-base-200 hover:bg-base-50 flex w-full items-center gap-2 rounded-lg bg-white px-3 py-2 text-left outline-none ring-1 transition focus-visible:ring-2 focus-visible:ring-indigo-500'
      >
        <span className='text-base-900 shrink-0 text-sm font-medium'>{selected.label}</span>
        <span className='text-base-500 min-w-0 flex-1 truncate text-xs'>{selected.description}</span>
        <Icon
          name='hero-chevron-down-mini'
          className={`text-base-400 h-4 w-4 shrink-0 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
        />
      </button>
      {isExpanded && (
        <div
          ref={listRef}
          id={listId}
          role='listbox'
          aria-label={label}
          onKeyDown={moveFocus}
          className='ring-base-200 mt-1 rounded-lg bg-white p-1 ring-1'
        >
          {options.map(option => {
            const isSelected = option.value === selected.value;

            return (
              <button
                key={option.value}
                type='button'
                role='option'
                aria-selected={isSelected}
                onClick={() => choose(option.value)}
                className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left outline-none transition focus-visible:ring-2 focus-visible:ring-indigo-500 ${
                  isSelected ? 'bg-base-100' : 'hover:bg-base-50'
                }`}
              >
                <span className='min-w-0 flex-1'>
                  <span className='text-base-900 block text-sm font-medium'>{option.label}</span>
                  <span className='text-base-500 block text-xs'>{option.description}</span>
                </span>
                {isSelected && <Icon name='hero-check-mini' className='h-4 w-4 shrink-0 text-indigo-600' />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function SegmentedControl<T extends string>({
  label,
  value,
  options,
  disabled = false,
  disabledHint,
  onChange,
}: {
  label: string;
  value: T;
  options: readonly { value: T; label: string; icon: IconName }[];
  disabled?: boolean;
  disabledHint?: string;
  onChange: (value: T) => void;
}) {
  const groupRef = useRef<HTMLDivElement>(null);
  const selectedIndex = Math.max(
    0,
    options.findIndex(option => option.value === value),
  );

  const moveSelection = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    const step = ({ ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 } as Record<string, number>)[event.key];
    if (!step) return;

    event.preventDefault();
    const nextIndex = (selectedIndex + step + options.length) % options.length;
    const next = options[nextIndex];
    if (!next) return;

    onChange(next.value);
    groupRef.current?.querySelectorAll<HTMLButtonElement>('[role="radio"]')[nextIndex]?.focus();
  };

  return (
    <div className='min-w-0'>
      <p className={OPTION_LABEL_CLASS}>{label}</p>
      <div
        ref={groupRef}
        role='radiogroup'
        aria-label={label}
        aria-disabled={disabled}
        onKeyDown={disabled ? undefined : moveSelection}
        className={`bg-base-100 grid gap-0.5 rounded-lg p-0.5 ${disabled ? 'opacity-60' : ''}`}
        style={{ gridTemplateColumns: `repeat(${options.length}, minmax(0, 1fr))` }}
      >
        {options.map((option, index) => {
          const isSelected = index === selectedIndex;

          return (
            <button
              key={option.value}
              type='button'
              role='radio'
              aria-checked={isSelected}
              tabIndex={isSelected ? 0 : -1}
              disabled={disabled}
              onClick={() => onChange(option.value)}
              className={`flex items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-semibold outline-none transition focus-visible:ring-2 focus-visible:ring-indigo-500 disabled:cursor-not-allowed ${
                isSelected
                  ? 'text-base-900 ring-base-200 shadow-xs bg-white ring-1'
                  : 'text-base-600 enabled:hover:text-base-900'
              }`}
            >
              <Icon name={option.icon} className='h-4 w-4 shrink-0' />
              {option.label}
            </button>
          );
        })}
      </div>
      {disabled && disabledHint && <p className='text-base-500 mt-1.5 text-xs'>{disabledHint}</p>}
    </div>
  );
}

function createId() {
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function getWebpEncodingMode(source: SourceType): WebpEncodingMode {
  return source === 'color' ? 'lossless' : 'lossy';
}

function getWebpEncodeOptions(encodingMode: WebpEncodingMode) {
  if (encodingMode === 'lossless') {
    return {
      lossless: 1 as const,
      quality: 100,
      method: 4,
      alpha_quality: 100,
    };
  }

  return {
    lossless: 0 as const,
    quality: LOSSY_WEBP_QUALITY,
    method: 4,
    alpha_quality: LOSSY_WEBP_ALPHA_QUALITY,
  };
}

function getCanvasWebpQuality(encodingMode: WebpEncodingMode): number {
  return encodingMode === 'lossless' ? 1 : LOSSY_WEBP_QUALITY / 100;
}

function getCropAspectRatio(ratioWidth: number, ratioHeight: number): number {
  const safeWidth = Math.max(1, Math.round(ratioWidth));
  const safeHeight = Math.max(1, Math.round(ratioHeight));
  return safeWidth / safeHeight;
}

function getCropPercentAspectRatio(
  ratioWidth: number,
  ratioHeight: number,
  sourceWidth: number,
  sourceHeight: number,
): number {
  const targetAspectRatio = getCropAspectRatio(ratioWidth, ratioHeight);
  const sourceAspectRatio = getCropAspectRatio(sourceWidth, sourceHeight);
  return targetAspectRatio / sourceAspectRatio;
}

function createDefaultCropRect(
  ratioWidth: number,
  ratioHeight: number,
  sourceWidth: number,
  sourceHeight: number,
): CropRect {
  const percentAspectRatio = getCropPercentAspectRatio(ratioWidth, ratioHeight, sourceWidth, sourceHeight);

  if (percentAspectRatio >= 1) {
    const height = Math.max(1, 100 / percentAspectRatio);

    return {
      x: 0,
      y: (100 - height) / 2,
      width: 100,
      height,
    };
  }

  const width = Math.max(1, 100 * percentAspectRatio);

  return {
    x: (100 - width) / 2,
    y: 0,
    width,
    height: 100,
  };
}

function areCropRectsEqual(left: CropRect, right: CropRect): boolean {
  return (
    Math.abs(left.x - right.x) < 0.01 &&
    Math.abs(left.y - right.y) < 0.01 &&
    Math.abs(left.width - right.width) < 0.01 &&
    Math.abs(left.height - right.height) < 0.01
  );
}

async function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result === 'string') {
        resolve(result);
      } else {
        reject(new Error('Invalid file content'));
      }
    };
    reader.onerror = () => reject(reader.error ?? new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

async function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result === 'string') {
        resolve(result);
      } else {
        reject(new Error('Invalid blob content'));
      }
    };
    reader.onerror = () => reject(reader.error ?? new Error('Failed to convert blob'));
    reader.readAsDataURL(blob);
  });
}

async function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality?: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      blob => {
        if (blob) {
          resolve(blob);
          return;
        }

        reject(new Error('Failed to create preview blob'));
      },
      type,
      quality,
    );
  });
}

async function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = url;
  });
}

async function loadRemoteImageAsDataUrl(url: string): Promise<string> {
  let response: Response;

  try {
    response = await fetch(url);
  } catch (_error) {
    throw new Error('Failed to load remote image. This host likely blocks browser access (CORS)');
  }

  if (!response.ok) {
    throw new Error(`Remote image request failed (${response.status})`);
  }

  const blob = await response.blob();
  return blobToDataUrl(blob);
}

async function createColorDataUrl(color: string, width: number, height: number): Promise<string> {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('Canvas context unavailable');
  }

  ctx.fillStyle = color;
  ctx.fillRect(0, 0, width, height);

  return canvas.toDataURL('image/png');
}

function rgbToHex(value: number): string {
  return Math.max(0, Math.min(255, Math.round(value)))
    .toString(16)
    .padStart(2, '0')
    .toUpperCase();
}

function parseHexColor(color: string): [number, number, number] | null {
  const normalized = color.trim().replace(/^#/, '');
  if (!/^[0-9A-Fa-f]{6}$/.test(normalized)) {
    return null;
  }

  return [
    Number.parseInt(normalized.slice(0, 2), 16),
    Number.parseInt(normalized.slice(2, 4), 16),
    Number.parseInt(normalized.slice(4, 6), 16),
  ];
}

function getColorDistance(left: string, right: string): number {
  const leftRgb = parseHexColor(left);
  const rightRgb = parseHexColor(right);
  if (!leftRgb || !rightRgb) {
    return Number.POSITIVE_INFINITY;
  }

  return Math.abs(leftRgb[0] - rightRgb[0]) + Math.abs(leftRgb[1] - rightRgb[1]) + Math.abs(leftRgb[2] - rightRgb[2]);
}

function normalizeHydratedSelectedColor(color: string): string {
  const normalized = color.trim().toUpperCase();
  const exactPreset = COLOR_PRESETS.find(preset => preset.toUpperCase() === normalized);
  if (exactPreset) {
    return exactPreset;
  }

  const closestPreset = COLOR_PRESETS.reduce<{ color: string; distance: number } | null>((closest, preset) => {
    const distance = getColorDistance(normalized, preset);
    if (closest === null || distance < closest.distance) {
      return { color: preset, distance };
    }

    return closest;
  }, null);

  return closestPreset && closestPreset.distance <= 3 ? closestPreset.color : normalized;
}

async function extractHexColorFromImageSource(source: string | Blob): Promise<string | null> {
  const objectUrl = typeof source === 'string' ? null : URL.createObjectURL(source);
  const imageUrl = objectUrl ?? (source as string);

  try {
    const image = await loadImage(imageUrl);
    const canvas = document.createElement('canvas');
    canvas.width = 1;
    canvas.height = 1;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });

    if (!ctx) {
      return null;
    }

    ctx.drawImage(image, 0, 0, 1, 1);
    const pixel = ctx.getImageData(0, 0, 1, 1).data;
    return `#${rgbToHex(pixel[0] ?? 0)}${rgbToHex(pixel[1] ?? 0)}${rgbToHex(pixel[2] ?? 0)}`;
  } catch {
    return null;
  } finally {
    if (objectUrl) {
      URL.revokeObjectURL(objectUrl);
    }
  }
}

function clampCrop(crop: CropRect): CropRect {
  const safeWidth = Math.min(100, Math.max(1, crop.width));
  const safeHeight = Math.min(100, Math.max(1, crop.height));
  const safeX = Math.min(100 - safeWidth, Math.max(0, crop.x));
  const safeY = Math.min(100 - safeHeight, Math.max(0, crop.y));

  return {
    x: safeX,
    y: safeY,
    width: safeWidth,
    height: safeHeight,
  };
}

function applyFreeCropDrag(
  mode: CropDragMode,
  initialCrop: CropRect,
  deltaXPercent: number,
  deltaYPercent: number,
): CropRect {
  const minSize = 5;
  let left = initialCrop.x;
  let top = initialCrop.y;
  let right = initialCrop.x + initialCrop.width;
  let bottom = initialCrop.y + initialCrop.height;

  if (mode === 'resize-top-left' || mode === 'resize-bottom-left') {
    left = Math.min(right - minSize, Math.max(0, left + deltaXPercent));
  } else {
    right = Math.max(left + minSize, Math.min(100, right + deltaXPercent));
  }

  if (mode === 'resize-top-left' || mode === 'resize-top-right') {
    top = Math.min(bottom - minSize, Math.max(0, top + deltaYPercent));
  } else {
    bottom = Math.max(top + minSize, Math.min(100, bottom + deltaYPercent));
  }

  return { x: left, y: top, width: right - left, height: bottom - top };
}

function exportSizeForCrop(
  crop: CropRect,
  sourceWidth: number,
  sourceHeight: number,
): { width: number; height: number } {
  const pixelWidth = Math.max(1, (crop.width / 100) * sourceWidth);
  const pixelHeight = Math.max(1, (crop.height / 100) * sourceHeight);
  const scale = EXPORT_LONG_EDGE / Math.max(pixelWidth, pixelHeight);

  return {
    width: Math.max(1, Math.round(pixelWidth * scale)),
    height: Math.max(1, Math.round(pixelHeight * scale)),
  };
}

function applyCropDrag(
  mode: CropDragMode,
  initialCrop: CropRect,
  deltaXPercent: number,
  deltaYPercent: number,
  cropRatioWidth: number,
  cropRatioHeight: number,
  sourceWidth: number,
  sourceHeight: number,
  lockAspect = true,
): CropRect {
  if (mode === 'move') {
    return clampCrop({
      ...initialCrop,
      x: initialCrop.x + deltaXPercent,
      y: initialCrop.y + deltaYPercent,
    });
  }

  if (!lockAspect) {
    return applyFreeCropDrag(mode, initialCrop, deltaXPercent, deltaYPercent);
  }

  const percentAspectRatio = getCropPercentAspectRatio(cropRatioWidth, cropRatioHeight, sourceWidth, sourceHeight);
  const minHeight = 5;
  const minWidth = minHeight * percentAspectRatio;
  const left = initialCrop.x;
  const top = initialCrop.y;
  const right = initialCrop.x + initialCrop.width;
  const bottom = initialCrop.y + initialCrop.height;

  const chooseNextWidth = (nextWidthFromX: number, nextWidthFromY: number, maxWidth: number) => {
    const preferredWidth =
      Math.abs(nextWidthFromX - initialCrop.width) >= Math.abs(nextWidthFromY - initialCrop.width)
        ? nextWidthFromX
        : nextWidthFromY;

    return Math.max(minWidth, Math.min(maxWidth, preferredWidth));
  };

  if (mode === 'resize-top-left') {
    const maxWidth = Math.min(right, bottom * percentAspectRatio);
    const nextWidth = chooseNextWidth(
      right - (left + deltaXPercent),
      (bottom - (top + deltaYPercent)) * percentAspectRatio,
      maxWidth,
    );
    const nextHeight = nextWidth / percentAspectRatio;

    return {
      x: right - nextWidth,
      y: bottom - nextHeight,
      width: nextWidth,
      height: nextHeight,
    };
  }

  if (mode === 'resize-top-right') {
    const maxWidth = Math.min(100 - left, bottom * percentAspectRatio);
    const nextWidth = chooseNextWidth(
      right + deltaXPercent - left,
      (bottom - (top + deltaYPercent)) * percentAspectRatio,
      maxWidth,
    );
    const nextHeight = nextWidth / percentAspectRatio;

    return {
      x: left,
      y: bottom - nextHeight,
      width: nextWidth,
      height: nextHeight,
    };
  }

  if (mode === 'resize-bottom-left') {
    const maxWidth = Math.min(right, (100 - top) * percentAspectRatio);
    const nextWidth = chooseNextWidth(
      right - (left + deltaXPercent),
      (bottom + deltaYPercent - top) * percentAspectRatio,
      maxWidth,
    );
    const nextHeight = nextWidth / percentAspectRatio;

    return {
      x: right - nextWidth,
      y: top,
      width: nextWidth,
      height: nextHeight,
    };
  }

  const maxWidth = Math.min(100 - left, (100 - top) * percentAspectRatio);
  const nextWidth = chooseNextWidth(
    right + deltaXPercent - left,
    (bottom + deltaYPercent - top) * percentAspectRatio,
    maxWidth,
  );
  const nextHeight = nextWidth / percentAspectRatio;

  return {
    x: left,
    y: top,
    width: nextWidth,
    height: nextHeight,
  };
}

function getCropSourceRect(
  crop: CropRect,
  imageWidth: number,
  imageHeight: number,
): { sx: number; sy: number; sw: number; sh: number } {
  const normalizedCrop = clampCrop(crop);

  return {
    sx: Math.floor((normalizedCrop.x / 100) * imageWidth),
    sy: Math.floor((normalizedCrop.y / 100) * imageHeight),
    sw: Math.max(1, Math.floor((normalizedCrop.width / 100) * imageWidth)),
    sh: Math.max(1, Math.floor((normalizedCrop.height / 100) * imageHeight)),
  };
}

function drawCroppedImageToCanvas(
  image: HTMLImageElement,
  crop: CropRect,
  targetWidth: number,
  targetHeight: number,
): HTMLCanvasElement {
  const { sx, sy, sw, sh } = getCropSourceRect(crop, image.naturalWidth, image.naturalHeight);

  const safeTargetWidth = Math.max(1, Math.floor(targetWidth));
  const safeTargetHeight = Math.max(1, Math.floor(targetHeight));

  const canvas = document.createElement('canvas');
  canvas.width = safeTargetWidth;
  canvas.height = safeTargetHeight;

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Canvas context unavailable');
  }

  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.clearRect(0, 0, safeTargetWidth, safeTargetHeight);
  ctx.drawImage(image, sx, sy, sw, sh, 0, 0, safeTargetWidth, safeTargetHeight);

  return canvas;
}

function extractCroppedImageData(image: HTMLImageElement, crop: CropRect): ImageData {
  const { sx, sy, sw, sh } = getCropSourceRect(crop, image.naturalWidth, image.naturalHeight);

  const canvas = document.createElement('canvas');
  canvas.width = sw;
  canvas.height = sh;

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Canvas context unavailable');
  }

  ctx.drawImage(image, sx, sy, sw, sh, 0, 0, sw, sh);

  return ctx.getImageData(0, 0, sw, sh);
}

function imageDataToCanvas(imageData: ImageData): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = imageData.width;
  canvas.height = imageData.height;

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Canvas context unavailable');
  }

  ctx.putImageData(imageData, 0, 0);

  return canvas;
}

function getPreviewTargetSize(targetWidth: number, targetHeight: number): { width: number; height: number } {
  const safeTargetWidth = Math.max(1, Math.floor(targetWidth));
  const safeTargetHeight = Math.max(1, Math.floor(targetHeight));
  const longEdge = Math.max(safeTargetWidth, safeTargetHeight);

  if (longEdge <= MAX_PREVIEW_LONG_EDGE) {
    return {
      width: safeTargetWidth,
      height: safeTargetHeight,
    };
  }

  const scale = MAX_PREVIEW_LONG_EDGE / longEdge;

  return {
    width: Math.max(1, Math.round(safeTargetWidth * scale)),
    height: Math.max(1, Math.round(safeTargetHeight * scale)),
  };
}

async function generatePreviewImage(
  image: HTMLImageElement,
  crop: CropRect,
  targetWidth: number,
  targetHeight: number,
): Promise<PreviewAsset> {
  const previewTarget = getPreviewTargetSize(targetWidth, targetHeight);
  const canvas = drawCroppedImageToCanvas(image, crop, previewTarget.width, previewTarget.height);

  let blob: Blob;
  try {
    blob = await canvasToBlob(canvas, 'image/webp', 0.82);
  } catch {
    blob = await canvasToBlob(canvas, 'image/jpeg', 0.86);
  }

  return {
    src: URL.createObjectURL(blob),
    blob,
    bytes: blob.size,
    mimeType: blob.type || 'image/webp',
  };
}

async function encodeCanvasToWebpBlob(canvas: HTMLCanvasElement, encodingMode: WebpEncodingMode): Promise<Blob> {
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Canvas context unavailable');
  }

  try {
    const { encode } = await import('@jsquash/webp');
    const rawImage = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const webpBuffer = await encode(rawImage, getWebpEncodeOptions(encodingMode));
    return new Blob([webpBuffer], { type: 'image/webp' });
  } catch {
    return canvasToBlob(canvas, 'image/webp', getCanvasWebpQuality(encodingMode));
  }
}

async function generateProcessedVariant(
  workingImage: WorkingImage,
  crop: CropRect,
  targetWidth: number,
  targetHeight: number,
  loadedImage?: HTMLImageElement,
  scale: Exclude<VariantScale, 0> = 1,
  encodingMode: WebpEncodingMode = 'lossy',
): Promise<ExportedVariant> {
  const image = loadedImage ?? (await loadImage(workingImage.dataUrl));
  const safeTargetWidth = Math.max(1, Math.floor(targetWidth));
  const safeTargetHeight = Math.max(1, Math.floor(targetHeight));
  const descriptor = scale === 1 ? '1x' : '2x';

  let canvas: HTMLCanvasElement;
  try {
    const croppedImageData = extractCroppedImageData(image, crop);
    const resizeModule = await getResizeModule();
    const resize = resizeModule.default;
    const resizedImageData = await resize(croppedImageData, {
      width: safeTargetWidth,
      height: safeTargetHeight,
      method: 'lanczos3',
      fitMethod: 'stretch',
      linearRGB: true,
      premultiply: true,
    });

    canvas = imageDataToCanvas(resizedImageData);
  } catch (error) {
    console.warn('ImageUploader resize fallback', error);
    canvas = drawCroppedImageToCanvas(image, crop, safeTargetWidth, safeTargetHeight);
  }

  const blob = await encodeCanvasToWebpBlob(canvas, encodingMode);

  return {
    scale,
    descriptor,
    blob,
    width: canvas.width,
    height: canvas.height,
    bytes: blob.size,
    mimeType: blob.type || 'image/webp',
  };
}

async function generateEditableVariant(
  workingImage: WorkingImage,
  loadedImage?: HTMLImageElement,
): Promise<ExportedVariant> {
  const sourceImage = loadedImage ?? (await loadImage(workingImage.dataUrl));
  const response = await fetch(workingImage.dataUrl);
  const blob = await response.blob();

  return {
    scale: 0,
    descriptor: 'editable',
    blob,
    width: sourceImage.naturalWidth,
    height: sourceImage.naturalHeight,
    bytes: blob.size,
    mimeType: blob.type || 'image/png',
  };
}

function isLegacyEditableVariant(
  editableVariant: Pick<ExportedVariant, 'width' | 'height' | 'bytes' | 'mimeType'> | undefined,
  primaryVariant: Pick<ExportedVariant, 'width' | 'height' | 'bytes' | 'mimeType'> | undefined,
): boolean {
  if (!editableVariant || !primaryVariant) {
    return false;
  }

  return (
    editableVariant.width === primaryVariant.width &&
    editableVariant.height === primaryVariant.height &&
    editableVariant.bytes === primaryVariant.bytes &&
    editableVariant.mimeType === primaryVariant.mimeType
  );
}

function serializeVariantSummary(
  variant: Pick<ExportedVariant, 'scale' | 'descriptor' | 'width' | 'height' | 'bytes' | 'mimeType'>,
) {
  return {
    scale: variant.scale,
    descriptor: variant.descriptor,
    width: variant.width,
    height: variant.height,
    bytes: variant.bytes,
    mimeType: variant.mimeType,
  };
}

async function readWorkingImage(
  dataUrl: string,
  source: SourceType,
  cacheAssetId?: string,
  attribution?: AssetAttribution,
): Promise<{ image: HTMLImageElement; workingImage: WorkingImage }> {
  const image = await loadImage(dataUrl);

  return {
    image,
    workingImage: {
      dataUrl,
      source,
      naturalWidth: image.naturalWidth,
      naturalHeight: image.naturalHeight,
      ...(cacheAssetId ? { cacheAssetId } : {}),
      ...(attribution ? { attribution } : {}),
    },
  };
}

function buildCacheSummary(manifest: ImageUploaderCacheManifestRecord): CachedAssetSummary {
  return {
    id: manifest.id,
    source: manifest.source,
    previewUrl: URL.createObjectURL(manifest.preview.blob),
    ...(getPersistedAssetRefIdentity(manifest.persisted)
      ? { persistedIdentity: getPersistedAssetRefIdentity(manifest.persisted)! }
      : {}),
    width: manifest.width,
    height: manifest.height,
    bytes: manifest.bytes,
    mimeType: manifest.mimeType,
    variants: manifest.variants.map(serializeVariantSummary),
    createdAt: manifest.createdAt,
    totalBytes: manifest.totalBytes,
    ...(manifest.attribution ? { attribution: manifest.attribution } : {}),
  };
}

function sortCacheItemsForDisplay(items: CachedAssetSummary[]): CachedAssetSummary[] {
  return [...items].sort((left, right) => Date.parse(right.createdAt) - Date.parse(left.createdAt));
}

function parsePersistedAssetValue(value?: string | PersistedAsset | null): PersistedAsset | null {
  if (!value) {
    return null;
  }

  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value) as PersistedAsset;
      if (parsed && typeof parsed === 'object') {
        return {
          ...parsed,
          variants: Array.isArray(parsed.variants) ? parsed.variants : [],
        };
      }
    } catch {
      return null;
    }

    return null;
  }

  if (typeof value === 'object') {
    return {
      ...value,
      variants: Array.isArray(value.variants) ? value.variants : [],
    };
  }

  return null;
}

function getPersistedAssetIdentity(asset: PersistedAsset | null | undefined): string | null {
  if (!asset) {
    return null;
  }

  return (
    asset.imageUploadId ??
    asset.url ??
    asset.variants.find(variant => variant.descriptor === '1x')?.imageUploadId ??
    asset.variants.find(variant => variant.descriptor === '1x')?.url ??
    asset.variants[0]?.imageUploadId ??
    asset.variants[0]?.url ??
    null
  );
}

function normalizeAssetUrl(url: string | null | undefined): string | null {
  if (!url) {
    return null;
  }

  try {
    if (typeof window === 'undefined') {
      return url;
    }

    return new URL(url, window.location.href).href;
  } catch {
    return url;
  }
}

function getPersistedAssetDisplayUrls(asset: PersistedAsset | null | undefined): string[] {
  if (!asset) {
    return [];
  }

  const urls = [asset.url, ...asset.variants.map(variant => variant.url)]
    .map(url => normalizeAssetUrl(url))
    .filter((url): url is string => Boolean(url));

  return [...new Set(urls)];
}

function getPersistedAssetRefIdentity(asset: ImageUploaderPersistedAssetRef | null | undefined): string | null {
  if (!asset) {
    return null;
  }

  return (
    asset.imageUploadId ??
    asset.url ??
    asset.variants.find(variant => variant.descriptor === '1x')?.imageUploadId ??
    asset.variants.find(variant => variant.descriptor === '1x')?.url ??
    asset.variants[0]?.imageUploadId ??
    asset.variants[0]?.url ??
    null
  );
}

function getWorkingImageIdentity(
  image: Pick<WorkingImage, 'persistedIdentity' | 'cacheAssetId'> | null | undefined,
): string | null {
  if (!image) {
    return null;
  }

  return image.persistedIdentity ?? image.cacheAssetId ?? null;
}

function closeHostModal(modalId: string): void {
  const modalRoot = document.getElementById(modalId);
  if (!modalRoot) return;

  const closeButton = modalRoot.querySelector<HTMLButtonElement>('[aria-label="close"]');
  closeButton?.click();
}

function toPersistedAssetRef(asset: PersistedAsset): ImageUploaderPersistedAssetRef {
  return {
    ...(asset.url ? { url: asset.url } : {}),
    ...(asset.filename ? { filename: asset.filename } : {}),
    ...(asset.imageUploadId ? { imageUploadId: asset.imageUploadId } : {}),
    variants: asset.variants.map(variant => ({
      scale: variant.scale,
      descriptor: variant.descriptor,
      ...(variant.url ? { url: variant.url } : {}),
      ...(variant.filename ? { filename: variant.filename } : {}),
      ...(variant.imageUploadId ? { imageUploadId: variant.imageUploadId } : {}),
    })),
  };
}

function buildPersistedAssetFromCacheRecord(
  record: Awaited<ReturnType<typeof getImageUploaderCacheRecord>> extends infer T
    ? T extends null
      ? never
      : NonNullable<T>
    : never,
  edit: ImageEditState,
  persisted: ImageUploaderPersistedAssetRef,
): PersistedAsset {
  return {
    source: record.source,
    width: record.width,
    height: record.height,
    bytes: record.bytes,
    mimeType: record.mimeType,
    createdAt: record.createdAt,
    edit,
    variants: record.variants.map(variant => {
      const persistedVariant = persisted.variants.find(
        candidate => candidate.descriptor === variant.descriptor && candidate.scale === variant.scale,
      );

      return {
        scale: variant.scale,
        descriptor: variant.descriptor,
        width: variant.width,
        height: variant.height,
        bytes: variant.bytes,
        mimeType: variant.mimeType,
        ...(persistedVariant?.url ? { url: persistedVariant.url } : {}),
        ...(persistedVariant?.filename ? { filename: persistedVariant.filename } : {}),
        ...(persistedVariant?.imageUploadId ? { imageUploadId: persistedVariant.imageUploadId } : {}),
      };
    }),
    ...(persisted.url ? { url: persisted.url } : {}),
    ...(persisted.filename ? { filename: persisted.filename } : {}),
    ...(persisted.imageUploadId ? { imageUploadId: persisted.imageUploadId } : {}),
    ...(record.attribution ? { attribution: record.attribution } : {}),
  };
}

function matchesPersistedAsset(manifest: ImageUploaderCacheManifestRecord, asset: PersistedAsset): boolean {
  const persisted = manifest.persisted;
  if (!persisted) {
    return false;
  }

  const manifestIdentity =
    persisted.imageUploadId ??
    persisted.url ??
    persisted.variants.find(variant => variant.descriptor === '1x')?.imageUploadId ??
    persisted.variants.find(variant => variant.descriptor === '1x')?.url ??
    null;

  const assetIdentity = getPersistedAssetIdentity(asset);
  return manifestIdentity !== null && assetIdentity !== null && manifestIdentity === assetIdentity;
}

async function fetchPersistedAssetSource(
  asset: PersistedAsset,
): Promise<{ objectUrl: string; usesEditableVariant: boolean }> {
  const editableVariantUrl = asset.variants.find(variant => variant.descriptor === 'editable')?.url;
  const sourceUrl = editableVariantUrl ?? asset.url;
  if (!sourceUrl) {
    throw new Error('Persisted asset has no editable source URL');
  }

  const response = await fetch(sourceUrl, { credentials: 'same-origin' });
  if (!response.ok) {
    throw new Error(`Failed to load saved image (${response.status})`);
  }

  const blob = await response.blob();
  return {
    objectUrl: URL.createObjectURL(blob),
    usesEditableVariant: Boolean(editableVariantUrl),
  };
}

function serializeAssetForEvent(asset: {
  source: SourceType;
  width: number;
  height: number;
  bytes: number;
  mimeType: string;
  createdAt: string;
  edit: ImageEditState;
  attribution?: AssetAttribution;
}) {
  return {
    source: asset.source,
    width: asset.width,
    height: asset.height,
    bytes: asset.bytes,
    mimeType: asset.mimeType,
    createdAt: asset.createdAt,
    edit: asset.edit,
    ...(asset.attribution ? { attribution: asset.attribution } : {}),
  };
}

async function uploadPreparedAssetToBackend(
  uploadUrl: string,
  variants: ExportedVariant[],
  asset: {
    source: SourceType;
    width: number;
    height: number;
    bytes: number;
    mimeType: string;
    createdAt: string;
    edit: ImageEditState;
    attribution?: AssetAttribution;
    variants: CachedVariantSummary[];
  },
): Promise<PersistedAsset> {
  const formData = new FormData();
  formData.append('metadata', JSON.stringify(serializeAssetForEvent(asset)));

  const primaryVariant = variants.find(variant => variant.descriptor === '1x') ?? variants[0];
  if (!primaryVariant) {
    throw new Error('No image variant available for upload');
  }
  formData.append('image', primaryVariant.blob, 'image.webp');

  const requestInit: RequestInit = {
    method: 'POST',
    credentials: 'same-origin',
    body: formData,
  };

  const csrfToken = getCsrfToken();
  if (csrfToken) {
    requestInit.headers = { 'x-csrf-token': csrfToken };
  }

  const response = await fetch(uploadUrl, requestInit);
  const payload = (await response.json().catch(() => null)) as { asset?: PersistedAsset; error?: string } | null;

  if (!response.ok) {
    throw new Error(payload?.error || 'Failed to upload image to backend');
  }

  if (!payload?.asset) {
    throw new Error('Backend did not return a persisted asset');
  }

  return {
    ...payload.asset,
    ...(asset.attribution ? { attribution: asset.attribution } : {}),
    variants: Array.isArray(payload.asset.variants) ? payload.asset.variants : [],
  };
}

export function ImageUploader({
  unsplashAccessKey,
  unsplashPerPage = DEFAULT_UNSPLASH_PER_PAGE,
  cacheKey = DEFAULT_CACHE_KEY,
  maxCachedItems = DEFAULT_MAX_CACHE_ITEMS,
  defaultTab = 'upload',
  config,
  closeModalId,
  eventName = 'image_uploader:asset_ready',
  target,
  uploadUrl,
  initialAsset,
  persistedValue,
  appliedImageSelector,
  pushEventTo,
}: ImageUploaderProps) {
  const fileInputId = useId();
  const customColorInputRef = useRef<HTMLInputElement | null>(null);
  const gallerySearchInputRef = useRef<HTMLInputElement | null>(null);
  const uploaderRootRef = useRef<HTMLDivElement | null>(null);
  const sourceImageRef = useRef<HTMLDivElement | null>(null);
  const workspaceRef = useRef<HTMLDivElement | null>(null);
  const workingImageElementRef = useRef<{ src: string; image: HTMLImageElement } | null>(null);
  const previewObjectUrlRef = useRef<string | null>(null);
  const cachePreviewUrlsRef = useRef<string[]>([]);
  const workingImageObjectUrlRef = useRef<string | null>(null);
  const customColorApplyTimeoutRef = useRef<number | null>(null);
  const hydratedPersistedAssetIdentityRef = useRef<string | null>(null);
  const unsplashSearchRequestIdRef = useRef(0);
  const saveToCacheRef = useRef<((options?: { closeHost?: boolean }) => Promise<boolean>) | null>(null);
  const canvasSrcBeforePreviewRef = useRef<string | null>(null);
  // Saving updates workingImage even when the upload fails, so a failed crop must not be retried as-is.
  const failedCropAutoSaveRef = useRef<{
    dataUrl: string;
    crop: CropRect;
    targetWidth: number;
    targetHeight: number;
  } | null>(null);
  const cropRef = useRef<CropRect>({ x: 0, y: 0, width: 100, height: 100 });
  const aspectLockedRef = useRef(true);
  const resolvedConfig = useMemo(() => normalizeImageUploaderConfig(config), [config]);
  // Prefer an explicitly passed persisted asset for reusable screens.
  // Fall back to the regular field value when the component is used inside flows.
  const persistedAsset = useMemo(
    () => parsePersistedAssetValue(initialAsset ?? persistedValue),
    [initialAsset, persistedValue],
  );
  const persistedAssetIdentity = useMemo(() => getPersistedAssetIdentity(persistedAsset), [persistedAsset]);
  const persistedAssetDisplayUrls = useMemo(() => getPersistedAssetDisplayUrls(persistedAsset), [persistedAsset]);
  const outputPresets = resolvedConfig.presets;
  const allowedSourceTabs = resolvedConfig.sources;
  const isFixedSize = resolvedConfig.fixedSize;
  const showPresetSelector = outputPresets.length > 1;
  const showSizeInputs = resolvedConfig.showSizeInputs;
  const showDisplayOptions = resolvedConfig.showDisplayOptions;
  const defaultTargetWidth = resolvedConfig.targetWidth;
  const defaultTargetHeight = resolvedConfig.targetHeight;
  const configuredCropRatioWidth = resolvedConfig.cropRatioWidth;
  const configuredCropRatioHeight = resolvedConfig.cropRatioHeight;
  const defaultSourceTab = resolveDefaultSourceTab(allowedSourceTabs, defaultTab);
  const [activeTab, setActiveTab] = useState<SourceType>(defaultSourceTab);
  const [workingImage, setWorkingImage] = useState<WorkingImage | null>(null);
  const [crop, setCrop] = useState<CropRect>({ x: 0, y: 0, width: 100, height: 100 });
  const [cropDragState, setCropDragState] = useState<CropDragState | null>(null);
  const [targetWidth, setTargetWidth] = useState<number>(defaultTargetWidth);
  const [targetHeight, setTargetHeight] = useState<number>(defaultTargetHeight);
  const cropRatioWidth = configuredCropRatioWidth ?? targetWidth;
  const cropRatioHeight = configuredCropRatioHeight ?? targetHeight;
  const originalAspectPreset = outputPresets.find(preset => preset.original) ?? null;
  const isOriginalAspectActive =
    workingImage !== null &&
    workingImage.source !== 'color' &&
    isFullCrop(crop) &&
    Math.abs(targetWidth / targetHeight - workingImage.naturalWidth / workingImage.naturalHeight) < 0.01;
  const hasPersistedAsset = persistedAsset !== null;
  const persistedDisplaySize = persistedAsset?.display?.size;
  const persistedDisplayAlign = persistedAsset?.display?.align;
  const persistedAlt = persistedAsset?.alt ?? '';
  // Assets saved before display options existed have no settings; leave them unset
  // so the form keeps rendering them at their stored size until one is picked.
  const [displaySettings, setDisplaySettings] = useState<DisplaySettings | null>(() =>
    persistedAsset ? normalizeDisplaySettings(persistedAsset.display) : DEFAULT_DISPLAY_SETTINGS,
  );
  const effectiveDisplay = displaySettings ?? DEFAULT_DISPLAY_SETTINGS;
  // Not persisted. A reopened image starts locked to the shape that was saved.
  const [aspectLocked, setAspectLocked] = useState(true);
  aspectLockedRef.current = aspectLocked;
  cropRef.current = crop;

  const [previewAsset, setPreviewAsset] = useState<PreviewAsset | null>(null);
  const [isGeneratingPreview, setIsGeneratingPreview] = useState<boolean>(false);
  const [isSavingToCache, setIsSavingToCache] = useState<boolean>(false);

  const [linkInput, setLinkInput] = useState<string>('');
  const [unsplashQuery, setUnsplashQuery] = useState<string>('');
  const [lastUnsplashQuery, setLastUnsplashQuery] = useState<string>('');
  const [unsplashResults, setUnsplashResults] = useState<UnsplashPhoto[]>([]);
  const [unsplashPage, setUnsplashPage] = useState<number>(1);
  const [hasMoreUnsplashResults, setHasMoreUnsplashResults] = useState<boolean>(false);
  const [selectedUnsplashPhotoId, setSelectedUnsplashPhotoId] = useState<string | null>(null);
  const [loadedGalleryImageIds, setLoadedGalleryImageIds] = useState<Record<string, true>>({});
  const [isUnsplashLoading, setIsUnsplashLoading] = useState<boolean>(false);
  const [isUnsplashLoadingMore, setIsUnsplashLoadingMore] = useState<boolean>(false);
  const [isUploadDragging, setIsUploadDragging] = useState<boolean>(false);
  const [selectedColor, setSelectedColor] = useState<string>(DEFAULT_SELECTED_COLOR);
  const [customColors, setCustomColors] = useState<string[]>([]);
  const [applyingColorValue, setApplyingColorValue] = useState<string | null>(null);
  const [applyingCacheItemId, setApplyingCacheItemId] = useState<string | null>(null);
  const [pendingRecentSelectionId, setPendingRecentSelectionId] = useState<string | null>(null);
  const [visiblePersistedAssetIdentity, setVisiblePersistedAssetIdentity] = useState<string | null>(null);
  const [visibleGallerySourceUrl, setVisibleGallerySourceUrl] = useState<string | null>(null);
  const [isRecentTabActive, setIsRecentTabActive] = useState<boolean>(false);

  const [cacheItems, setCacheItems] = useState<CachedAssetSummary[]>([]);
  const [isSourceChooserOpen, setIsSourceChooserOpen] = useState<boolean>(true);
  const [pendingAutoApply, setPendingAutoApply] = useState<PendingAutoApply | null>(null);
  const [appliedEditorState, setAppliedEditorState] = useState<AppliedEditorState | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    if (targetWidth !== defaultTargetWidth) {
      setTargetWidth(defaultTargetWidth);
    }

    if (targetHeight !== defaultTargetHeight) {
      setTargetHeight(defaultTargetHeight);
    }
  }, [defaultTargetHeight, defaultTargetWidth]);

  useEffect(() => {
    let cancelled = false;

    const loadCache = async () => {
      try {
        const manifests = await listImageUploaderCacheManifests(cacheKey);
        if (cancelled) return;
        replaceCacheItems(manifests.map(buildCacheSummary));
      } catch (error) {
        if (cancelled) return;
        const message = error instanceof Error ? error.message : 'Failed to load image cache';
        setErrorMessage(message);
      }
    };

    void loadCache();

    return () => {
      cancelled = true;
    };
  }, [cacheKey]);

  useEffect(() => {
    setCustomColors(readStoredCustomColors(cacheKey));
  }, [cacheKey]);

  useEffect(() => {
    setDisplaySettings(
      hasPersistedAsset
        ? normalizeDisplaySettings({ size: persistedDisplaySize, align: persistedDisplayAlign })
        : DEFAULT_DISPLAY_SETTINGS,
    );
  }, [hasPersistedAsset, persistedDisplaySize, persistedDisplayAlign]);

  useEffect(() => {
    writeStoredCustomColors(cacheKey, customColors);
  }, [cacheKey, customColors]);

  useEffect(() => {
    const persistedIdentity = getPersistedAssetIdentity(persistedAsset);
    if (!persistedAsset || !persistedIdentity) {
      hydratedPersistedAssetIdentityRef.current = null;
      setAppliedEditorState(null);
      return;
    }

    if (hydratedPersistedAssetIdentityRef.current === persistedIdentity) {
      return;
    }

    let cancelled = false;

    const hydratePersistedAsset = async () => {
      const manifests = await listImageUploaderCacheManifests(cacheKey);
      if (cancelled) return;

      const matchingManifest = manifests.find(manifest => matchesPersistedAsset(manifest, persistedAsset));
      if (matchingManifest) {
        const record = await getImageUploaderCacheRecord(cacheKey, matchingManifest.id);
        if (cancelled) return;

        if (record) {
          const editableVariant = record.variants.find(variant => variant.descriptor === 'editable');
          const primaryVariant = record.variants.find(variant => variant.descriptor === '1x') ?? record.variants[0];
          const usesEditableVariant = !isLegacyEditableVariant(editableVariant, primaryVariant);
          const editorVariant = usesEditableVariant ? (editableVariant ?? primaryVariant) : primaryVariant;
          const edit = record.edit ??
            persistedAsset.edit ?? {
              crop: createConfiguredDefaultCropRect(
                editorVariant?.width ?? primaryVariant?.width ?? defaultTargetWidth,
                editorVariant?.height ?? primaryVariant?.height ?? defaultTargetHeight,
                primaryVariant?.width ?? defaultTargetWidth,
                primaryVariant?.height ?? defaultTargetHeight,
              ),
              targetWidth: primaryVariant?.width ?? defaultTargetWidth,
              targetHeight: primaryVariant?.height ?? defaultTargetHeight,
            };

          if (!primaryVariant || !editorVariant) {
            throw new Error('Cached item has no usable variant');
          }

          revokeWorkingImageObjectUrl();
          workingImageElementRef.current = null;

          const sourceUrl = URL.createObjectURL(editorVariant.blob);
          workingImageObjectUrlRef.current = sourceUrl;

          replacePreview({
            src: URL.createObjectURL(record.preview.blob),
            blob: record.preview.blob,
            bytes: record.preview.bytes,
            mimeType: record.preview.mimeType,
          });

          setWorkingImage({
            dataUrl: sourceUrl,
            source: record.source,
            naturalWidth: editorVariant.width,
            naturalHeight: editorVariant.height,
            cacheAssetId: record.id,
            persistedIdentity,
            ...(record.attribution ? { attribution: record.attribution } : {}),
          });
          const hydratedCrop = usesEditableVariant ? edit.crop : { x: 0, y: 0, width: 100, height: 100 };
          const hydratedTargetWidth = usesEditableVariant
            ? isFixedSize
              ? defaultTargetWidth
              : edit.targetWidth
            : isFixedSize
              ? defaultTargetWidth
              : primaryVariant.width;
          const hydratedTargetHeight = usesEditableVariant
            ? isFixedSize
              ? defaultTargetHeight
              : edit.targetHeight
            : isFixedSize
              ? defaultTargetHeight
              : primaryVariant.height;
          setTargetWidth(hydratedTargetWidth);
          setTargetHeight(hydratedTargetHeight);
          setCrop(hydratedCrop);
          setActiveTab(defaultSourceTab);
          setIsSourceChooserOpen(true);
          if (record.source === 'color') {
            const hydratedColor = await extractHexColorFromImageSource(sourceUrl);
            if (!cancelled && hydratedColor) {
              setSelectedColor(normalizeHydratedSelectedColor(hydratedColor));
            }
          }
          setSelectedUnsplashPhotoId(null);
          setErrorMessage('');

          await touchImageUploaderCacheRecord(cacheKey, record.id);
          if (cancelled) return;
          hydratedPersistedAssetIdentityRef.current = persistedIdentity;
          setAppliedEditorState({
            imageIdentity: persistedIdentity,
            crop: hydratedCrop,
            targetWidth: hydratedTargetWidth,
            targetHeight: hydratedTargetHeight,
          });
          return;
        }
      }

      revokeWorkingImageObjectUrl();
      workingImageElementRef.current = null;

      const { objectUrl: sourceUrl, usesEditableVariant } = await fetchPersistedAssetSource(persistedAsset);
      if (cancelled) {
        URL.revokeObjectURL(sourceUrl);
        return;
      }

      workingImageObjectUrlRef.current = sourceUrl;
      const nextImage = await readWorkingImage(sourceUrl, persistedAsset.source, undefined, persistedAsset.attribution);
      if (cancelled) {
        URL.revokeObjectURL(sourceUrl);
        return;
      }

      workingImageElementRef.current = {
        src: nextImage.workingImage.dataUrl,
        image: nextImage.image,
      };
      // Backend fallback only gives us the already-cropped 1x asset, so
      // reapplying the saved crop would double-crop the preview after reload.
      const hydratedCrop = usesEditableVariant ? persistedAsset.edit.crop : { x: 0, y: 0, width: 100, height: 100 };
      const hydratedTargetWidth = isFixedSize ? defaultTargetWidth : persistedAsset.width;
      const hydratedTargetHeight = isFixedSize ? defaultTargetHeight : persistedAsset.height;
      replacePreview(null);
      setWorkingImage({ ...nextImage.workingImage, persistedIdentity });
      setTargetWidth(hydratedTargetWidth);
      setTargetHeight(hydratedTargetHeight);
      setCrop(hydratedCrop);
      setActiveTab(defaultSourceTab);
      setIsSourceChooserOpen(true);
      if (persistedAsset.source === 'color') {
        const hydratedColor = await extractHexColorFromImageSource(sourceUrl);
        if (!cancelled && hydratedColor) {
          setSelectedColor(normalizeHydratedSelectedColor(hydratedColor));
        }
      }
      setSelectedUnsplashPhotoId(null);
      setErrorMessage('');
      hydratedPersistedAssetIdentityRef.current = persistedIdentity;
      setAppliedEditorState({
        imageIdentity: persistedIdentity,
        crop: hydratedCrop,
        targetWidth: hydratedTargetWidth,
        targetHeight: hydratedTargetHeight,
      });
    };

    void hydratePersistedAsset().catch(error => {
      if (cancelled) return;
      const message = error instanceof Error ? error.message : 'Failed to load saved image';
      setErrorMessage(message);
    });

    return () => {
      cancelled = true;
    };
  }, [cacheKey, defaultSourceTab, defaultTargetHeight, defaultTargetWidth, isFixedSize, persistedAsset]);

  useEffect(() => {
    return () => {
      if (customColorApplyTimeoutRef.current !== null) {
        window.clearTimeout(customColorApplyTimeoutRef.current);
        customColorApplyTimeoutRef.current = null;
      }

      if (previewObjectUrlRef.current) {
        URL.revokeObjectURL(previewObjectUrlRef.current);
        previewObjectUrlRef.current = null;
      }

      if (workingImageObjectUrlRef.current) {
        URL.revokeObjectURL(workingImageObjectUrlRef.current);
        workingImageObjectUrlRef.current = null;
      }

      for (const previewUrl of cachePreviewUrlsRef.current) {
        URL.revokeObjectURL(previewUrl);
      }
      cachePreviewUrlsRef.current = [];
    };
  }, []);

  function revokeWorkingImageObjectUrl() {
    if (workingImageObjectUrlRef.current) {
      URL.revokeObjectURL(workingImageObjectUrlRef.current);
      workingImageObjectUrlRef.current = null;
    }
  }

  function replaceCacheItems(nextItems: CachedAssetSummary[]) {
    for (const previewUrl of cachePreviewUrlsRef.current) {
      URL.revokeObjectURL(previewUrl);
    }

    const sortedItems = sortCacheItemsForDisplay(nextItems);
    cachePreviewUrlsRef.current = sortedItems.map(item => item.previewUrl);
    setCacheItems(sortedItems);
  }

  function replacePreview(nextPreview: PreviewAsset | null) {
    if (previewObjectUrlRef.current) {
      URL.revokeObjectURL(previewObjectUrlRef.current);
      previewObjectUrlRef.current = null;
    }

    if (nextPreview?.src.startsWith('blob:')) {
      previewObjectUrlRef.current = nextPreview.src;
    }

    setPreviewAsset(nextPreview);
  }

  function createConfiguredDefaultCropRect(
    sourceWidth: number,
    sourceHeight: number,
    fallbackRatioWidth: number,
    fallbackRatioHeight: number,
  ): CropRect {
    return createDefaultCropRect(
      configuredCropRatioWidth ?? fallbackRatioWidth,
      configuredCropRatioHeight ?? fallbackRatioHeight,
      sourceWidth,
      sourceHeight,
    );
  }

  async function getWorkingImageElement(nextWorkingImage: WorkingImage): Promise<HTMLImageElement> {
    const cached = workingImageElementRef.current;
    if (cached && cached.src === nextWorkingImage.dataUrl) {
      return cached.image;
    }

    const image = await loadImage(nextWorkingImage.dataUrl);
    workingImageElementRef.current = {
      src: nextWorkingImage.dataUrl,
      image,
    };

    return image;
  }

  useEffect(() => {
    let cancelled = false;
    let timeoutId: number | null = null;

    async function buildPreview() {
      if (!workingImage) {
        replacePreview(null);
        setIsGeneratingPreview(false);
        return;
      }

      if (cropDragState) {
        setIsGeneratingPreview(false);
        return;
      }

      setIsGeneratingPreview(true);
      setErrorMessage('');

      try {
        const image = await getWorkingImageElement(workingImage);
        const output = await generatePreviewImage(image, crop, targetWidth, targetHeight);
        if (!cancelled) {
          replacePreview(output);
        }
      } catch (error) {
        if (!cancelled) {
          const message = error instanceof Error ? error.message : 'Failed to render preview';
          setErrorMessage(message);
          replacePreview(null);
        }
      } finally {
        if (!cancelled) {
          setIsGeneratingPreview(false);
        }
      }
    }

    timeoutId = window.setTimeout(() => {
      void buildPreview();
    }, PREVIEW_REBUILD_DELAY_MS);

    return () => {
      cancelled = true;
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [workingImage, crop, targetWidth, targetHeight, cropDragState]);

  useEffect(() => {
    if (!cropDragState) return;

    const onPointerMove = (event: PointerEvent) => {
      if (event.pointerId !== cropDragState.pointerId) return;

      const sourceEl = sourceImageRef.current;
      if (!sourceEl) return;

      const rect = sourceEl.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) return;

      const deltaXPercent = ((event.clientX - cropDragState.startX) / rect.width) * 100;
      const deltaYPercent = ((event.clientY - cropDragState.startY) / rect.height) * 100;

      const nextCrop = applyCropDrag(
        cropDragState.mode,
        cropDragState.initialCrop,
        deltaXPercent,
        deltaYPercent,
        cropRatioWidth,
        cropRatioHeight,
        workingImage?.naturalWidth ?? rect.width,
        workingImage?.naturalHeight ?? rect.height,
        aspectLockedRef.current,
      );
      cropRef.current = nextCrop;
      setCrop(nextCrop);
    };

    const onPointerEnd = (event: PointerEvent) => {
      if (event.pointerId !== cropDragState.pointerId) return;

      if (
        !aspectLockedRef.current &&
        cropDragState.mode !== 'move' &&
        workingImage &&
        !areCropRectsEqual(cropRef.current, cropDragState.initialCrop)
      ) {
        const size = exportSizeForCrop(cropRef.current, workingImage.naturalWidth, workingImage.naturalHeight);
        setTargetWidth(size.width);
        setTargetHeight(size.height);
      }

      setCropDragState(null);
    };

    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerEnd);
    window.addEventListener('pointercancel', onPointerEnd);

    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerEnd);
      window.removeEventListener('pointercancel', onPointerEnd);
    };
  }, [cropDragState, cropRatioHeight, cropRatioWidth]);

  const canGenerate = workingImage !== null;
  const hasSource = workingImage !== null;
  const isAppliedImageUnchanged =
    hasSource &&
    appliedEditorState !== null &&
    getWorkingImageIdentity(workingImage) === appliedEditorState.imageIdentity &&
    targetWidth === appliedEditorState.targetWidth &&
    targetHeight === appliedEditorState.targetHeight &&
    areCropRectsEqual(crop, appliedEditorState.crop);
  const shouldShowApplyButton = canGenerate && !isAppliedImageUnchanged;
  const shouldShowSourceChooser = !hasSource || isSourceChooserOpen;
  const showCropEditor = hasSource && workingImage?.source !== 'color';
  const showEditorWorkspace = hasSource && !shouldShowSourceChooser;
  const isEditTabActive = showCropEditor && !shouldShowSourceChooser;
  const showChooserTabBar = shouldShowSourceChooser || showCropEditor;
  const showSourcePreviewCard =
    shouldShowSourceChooser &&
    hasSource &&
    !isRecentTabActive &&
    (workingImage?.source === 'upload' || workingImage?.source === 'link') &&
    activeTab === workingImage.source;
  const showUploadSourcePreviewCard = showSourcePreviewCard && activeTab === 'upload';
  const showLinkSourcePreviewCard = showSourcePreviewCard && activeTab === 'link';
  const showRecentTab = cacheItems.length > 0 && (shouldShowSourceChooser || showCropEditor);
  const chooserTabs: ChooserTab[] = shouldShowSourceChooser
    ? [
        ...allowedSourceTabs,
        ...(showCropEditor ? (['edit'] as const) : []),
        ...(showRecentTab ? (['recent'] as const) : []),
      ]
    : [
        ...(showCropEditor ? (['edit'] as const) : []),
        ...allowedSourceTabs,
        ...(showRecentTab ? (['recent'] as const) : []),
      ];
  const firstChooserTabAfterEdit = chooserTabs[0] === 'edit' ? (chooserTabs[1] ?? null) : null;
  const shouldShowRecentImages = cacheItems.length > 0 && shouldShowSourceChooser && isRecentTabActive;
  const shouldShowSourcePanel = shouldShowSourceChooser && !isRecentTabActive;
  const shouldUseScrollableChooser = shouldShowSourceChooser;
  const isCropDirty = canGenerate && !isAppliedImageUnchanged;
  const sourcePreviewSrc = showSourcePreviewCard ? (previewAsset?.src ?? workingImage?.dataUrl ?? null) : null;
  const activeSelectedColor = applyingColorValue ?? (workingImage?.source === 'color' ? selectedColor : null);
  const normalizedSelectedColor = activeSelectedColor?.toLowerCase() ?? null;
  const colorOptions = useMemo(() => {
    const palette = [...COLOR_PRESETS];

    for (const color of customColors) {
      if (!palette.some(preset => preset.toUpperCase() === color.toUpperCase())) {
        palette.push(color);
      }
    }

    const currentCustomColor = activeSelectedColor ? normalizeHexColorValue(activeSelectedColor) : null;
    if (currentCustomColor && !palette.some(color => color.toUpperCase() === currentCustomColor)) {
      palette.push(currentCustomColor);
    }

    return palette;
  }, [activeSelectedColor, customColors]);
  const selectedRecentCacheItemId = useMemo(() => {
    if (!visiblePersistedAssetIdentity) {
      return null;
    }

    const matchingItem = cacheItems.find(item => item.persistedIdentity === visiblePersistedAssetIdentity);
    return matchingItem?.id ?? null;
  }, [cacheItems, visiblePersistedAssetIdentity]);
  const activeGalleryQuery = unsplashQuery.trim() || DEFAULT_GALLERY_QUERY;

  const savingLabel = (
    <span className='inline-flex items-center gap-2'>
      <Icon name='hero-arrow-path' className='h-4 w-4 animate-spin' />
      <span>Saving...</span>
    </span>
  );

  function focusVisibleGallerySearchInput() {
    if (activeTab !== 'unsplash' || !shouldShowSourceChooser) {
      return;
    }

    const input = gallerySearchInputRef.current;
    if (!input) {
      return;
    }

    const rect = input.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0 || document.activeElement === input) {
      return;
    }

    input.focus();
  }

  useEffect(() => {
    if (allowedSourceTabs.includes(activeTab)) {
      return;
    }

    setActiveTab(defaultSourceTab);
  }, [activeTab, allowedSourceTabs, defaultSourceTab]);

  useEffect(() => {
    if (!showRecentTab && isRecentTabActive) {
      setIsRecentTabActive(false);
    }
  }, [isRecentTabActive, showRecentTab]);

  useEffect(() => {
    if (!persistedAssetIdentity) {
      setVisiblePersistedAssetIdentity(null);
      setVisibleGallerySourceUrl(null);
      return;
    }

    const nextVisibleGallerySourceUrl = normalizeAssetUrl(
      persistedAsset?.source === 'unsplash' ? (persistedAsset.attribution?.sourceUrl ?? null) : null,
    );

    if (!appliedImageSelector) {
      setVisiblePersistedAssetIdentity(current =>
        current === persistedAssetIdentity ? current : persistedAssetIdentity,
      );
      setVisibleGallerySourceUrl(current =>
        current === nextVisibleGallerySourceUrl ? current : nextVisibleGallerySourceUrl,
      );
      return;
    }

    let cancelled = false;
    let frameId: number | null = null;

    const checkAppliedImage = () => {
      if (cancelled) {
        return;
      }

      const image = document.querySelector<HTMLImageElement>(appliedImageSelector);
      if (!image) {
        frameId = window.requestAnimationFrame(checkAppliedImage);
        return;
      }

      const currentImageUrl = normalizeAssetUrl(image.currentSrc || image.getAttribute('src'));
      const matchesPersistedAsset =
        persistedAssetDisplayUrls.length === 0 ||
        (currentImageUrl !== null && persistedAssetDisplayUrls.includes(currentImageUrl));

      if (matchesPersistedAsset && image.complete && image.naturalWidth > 0) {
        setVisiblePersistedAssetIdentity(current =>
          current === persistedAssetIdentity ? current : persistedAssetIdentity,
        );
        setVisibleGallerySourceUrl(current =>
          current === nextVisibleGallerySourceUrl ? current : nextVisibleGallerySourceUrl,
        );
        return;
      }

      frameId = window.requestAnimationFrame(checkAppliedImage);
    };

    checkAppliedImage();

    return () => {
      cancelled = true;
      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
      }
    };
  }, [appliedImageSelector, persistedAsset, persistedAssetDisplayUrls, persistedAssetIdentity]);

  useEffect(() => {
    if (!selectedUnsplashPhotoId) {
      return;
    }

    const selectedPhoto = unsplashResults.find(photo => photo.id === selectedUnsplashPhotoId);
    if (!selectedPhoto) {
      return;
    }

    if (normalizeAssetUrl(selectedPhoto.fullUrl) === visibleGallerySourceUrl) {
      setSelectedUnsplashPhotoId(null);
    }
  }, [selectedUnsplashPhotoId, unsplashResults, visibleGallerySourceUrl]);

  useEffect(() => {
    if (!pendingRecentSelectionId) {
      return;
    }

    if (selectedRecentCacheItemId === pendingRecentSelectionId) {
      setPendingRecentSelectionId(null);
    }
  }, [pendingRecentSelectionId, selectedRecentCacheItemId]);

  useEffect(() => {
    if (activeTab !== 'unsplash' || !shouldShowSourceChooser) {
      return;
    }

    const focusId = window.requestAnimationFrame(() => {
      focusVisibleGallerySearchInput();
    });

    return () => {
      window.cancelAnimationFrame(focusId);
    };
  }, [activeTab, shouldShowSourceChooser]);

  useEffect(() => {
    const popover = uploaderRootRef.current?.closest('[data-part="popover"]');
    if (!(popover instanceof HTMLElement)) {
      return;
    }

    const observer = new MutationObserver(() => {
      window.requestAnimationFrame(() => {
        focusVisibleGallerySearchInput();
      });
    });

    observer.observe(popover, {
      attributes: true,
      attributeFilter: ['hidden', 'class'],
    });

    return () => {
      observer.disconnect();
    };
  }, [activeTab, shouldShowSourceChooser]);

  useEffect(() => {
    const visibleIds = new Set(unsplashResults.map(photo => photo.id));

    setLoadedGalleryImageIds(current =>
      Object.fromEntries(
        Object.keys(current)
          .filter(id => visibleIds.has(id))
          .map(id => [id, true]),
      ),
    );
  }, [unsplashResults]);

  function handleUploadDragOver(event: ReactDragEvent<HTMLLabelElement>) {
    event.preventDefault();
    setIsUploadDragging(true);
  }

  function handleUploadDragLeave(event: ReactDragEvent<HTMLLabelElement>) {
    event.preventDefault();
    setIsUploadDragging(false);
  }

  function handleUploadDrop(event: ReactDragEvent<HTMLLabelElement>) {
    event.preventDefault();
    setIsUploadDragging(false);

    const file = event.dataTransfer.files?.[0] ?? null;
    void handleUploadFile(file);
  }

  function applyOutputPreset(preset: ImageUploaderPreset) {
    if (isFixedSize) {
      return;
    }

    const size = preset.original && workingImage ? getOriginalAspectTarget(preset.width, workingImage) : preset;
    setTargetWidth(size.width);
    setTargetHeight(size.height);
    if (workingImage && workingImage.source !== 'color') {
      setCrop(
        preset.original
          ? FULL_CROP
          : createConfiguredDefaultCropRect(
              workingImage.naturalWidth,
              workingImage.naturalHeight,
              size.width,
              size.height,
            ),
      );
    }
    setErrorMessage('');
  }

  function withDisplayFields(asset: PersistedAsset): PersistedAsset {
    return showDisplayOptions ? applyDisplayFields(asset, displaySettings, persistedAlt) : asset;
  }

  function pushDisplayFields(nextDisplay: DisplaySettings | null) {
    setDisplaySettings(nextDisplay);

    const targetEl = target ? document.getElementById(target) : null;
    if (!persistedAsset || !targetEl || !pushEventTo) return;

    pushEventTo(targetEl, eventName, { asset: applyDisplayFields(persistedAsset, nextDisplay, persistedAlt) });
  }

  async function applyWorkingImage(
    dataUrl: string,
    source: SourceType,
    attribution?: AssetAttribution,
    options?: {
      keepSourceChooserOpen?: boolean;
    },
  ): Promise<WorkingImage> {
    const keepOriginalAspect =
      originalAspectPreset !== null &&
      !isFixedSize &&
      (outputPresets.length === 1 || (workingImage ? isOriginalAspectActive : resolvedConfig.defaultsToOriginalAspect));
    revokeWorkingImageObjectUrl();
    const nextImage = await readWorkingImage(dataUrl, source, undefined, attribution);
    workingImageElementRef.current = {
      src: nextImage.workingImage.dataUrl,
      image: nextImage.image,
    };
    replacePreview(null);
    setWorkingImage(nextImage.workingImage);
    setAspectLocked(true);
    setIsSourceChooserOpen(options?.keepSourceChooserOpen === true);
    if (source !== 'unsplash') {
      setSelectedUnsplashPhotoId(null);
    }
    if (source === 'color') {
      setCrop(FULL_CROP);
    } else if (keepOriginalAspect) {
      const size = getOriginalAspectTarget(originalAspectPreset.width, nextImage.workingImage);
      setTargetWidth(size.width);
      setTargetHeight(size.height);
      setCrop(FULL_CROP);
    } else {
      setCrop(
        createConfiguredDefaultCropRect(
          nextImage.workingImage.naturalWidth,
          nextImage.workingImage.naturalHeight,
          targetWidth,
          targetHeight,
        ),
      );
    }
    setErrorMessage('');
    return nextImage.workingImage;
  }

  async function handleUploadFile(file: File | null): Promise<void> {
    if (!file) return;

    try {
      const dataUrl = await fileToDataUrl(file);
      const nextWorkingImage = await applyWorkingImage(dataUrl, 'upload');
      setPendingAutoApply({ dataUrl: nextWorkingImage.dataUrl, closeHost: false });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load uploaded image';
      setErrorMessage(message);
    }
  }

  async function handleLoadFromLink(): Promise<void> {
    if (!linkInput.trim()) {
      setErrorMessage('Paste image URL first.');
      return;
    }

    try {
      const dataUrl = await loadRemoteImageAsDataUrl(linkInput.trim());
      const nextWorkingImage = await applyWorkingImage(dataUrl, 'link', {
        author: 'External URL',
        authorUrl: linkInput.trim(),
        sourceUrl: linkInput.trim(),
      });
      setPendingAutoApply({ dataUrl: nextWorkingImage.dataUrl, closeHost: false });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load remote image';
      setErrorMessage(message);
    }
  }

  async function handleSelectColor(color: string): Promise<void> {
    if (customColorApplyTimeoutRef.current !== null) {
      window.clearTimeout(customColorApplyTimeoutRef.current);
      customColorApplyTimeoutRef.current = null;
    }

    setSelectedColor(color);
    setApplyingColorValue(color);

    try {
      const dataUrl = await createColorDataUrl(color, defaultTargetWidth, defaultTargetHeight);
      const shouldKeepChooserOpen = isSourceChooserOpen;
      const nextWorkingImage = await applyWorkingImage(dataUrl, 'color', undefined, {
        keepSourceChooserOpen: shouldKeepChooserOpen,
      });
      if (isSourceChooserOpen) {
        setPendingAutoApply({ dataUrl: nextWorkingImage.dataUrl, closeHost: false });
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to create color image';
      setErrorMessage(message);
    } finally {
      setApplyingColorValue(current => (current?.toLowerCase() === color.toLowerCase() ? null : current));
    }
  }

  function rememberCustomColor(color: string) {
    const normalized = normalizeHexColorValue(color);
    if (!normalized || COLOR_PRESETS.some(preset => preset.toUpperCase() === normalized)) {
      return;
    }

    setCustomColors(current => {
      const next = [...current.filter(entry => entry.toUpperCase() !== normalized), normalized].slice(
        -MAX_CUSTOM_COLOR_SWATCHES,
      );

      if (next.length === current.length && next.every((entry, index) => entry === current[index])) {
        return current;
      }

      return next;
    });
  }

  function handleCustomColorPicked(color: string) {
    const normalized = normalizeHexColorValue(color);
    if (!normalized) {
      return;
    }

    setSelectedColor(normalized);
    rememberCustomColor(normalized);
    void handleSelectColor(normalized);
  }

  async function runUnsplashSearch(options?: {
    page?: number;
    append?: boolean;
    query?: string;
    cacheDefaultResults?: boolean;
  }): Promise<void> {
    if (!unsplashAccessKey) {
      setErrorMessage('Gallery key missing. Pass unsplashAccessKey prop for real search.');
      return;
    }

    const append = options?.append ?? false;
    const page = Math.max(1, options?.page ?? 1);
    const requestedQuery = (options?.query ?? unsplashQuery).trim();
    const query = append && lastUnsplashQuery ? lastUnsplashQuery : requestedQuery || DEFAULT_GALLERY_QUERY;
    const requestId = ++unsplashSearchRequestIdRef.current;
    const shouldCacheDefaultResults = options?.cacheDefaultResults === true || query === DEFAULT_GALLERY_QUERY;

    setErrorMessage('');

    if (append) {
      setIsUnsplashLoadingMore(true);
    } else {
      setIsUnsplashLoading(true);
    }

    try {
      const url = new URL('https://api.unsplash.com/search/photos');
      url.searchParams.set('query', query);
      url.searchParams.set('page', String(page));
      const perPage = Math.max(1, Math.min(30, unsplashPerPage));
      url.searchParams.set('per_page', String(perPage));
      url.searchParams.set('orientation', 'landscape');

      const response = await fetch(url.toString(), {
        headers: {
          Authorization: `Client-ID ${unsplashAccessKey}`,
        },
      });

      if (!response.ok) {
        if (requestId !== unsplashSearchRequestIdRef.current) {
          return;
        }
        setErrorMessage(`Gallery request failed (${response.status})`);
        if (!append && unsplashResults.length === 0) {
          setUnsplashResults([]);
          setHasMoreUnsplashResults(false);
        }
        return;
      }

      const payload = (await response.json()) as {
        total_pages?: number;
        results?: Array<{
          id: string;
          urls?: { small?: string; regular?: string };
          user?: { name?: string; links?: { html?: string } };
          links?: { html?: string };
        }>;
      };

      const items = (payload.results ?? [])
        .map(item => {
          const thumbUrl = item.urls?.small;
          const fullUrl = item.urls?.regular;

          if (!thumbUrl || !fullUrl) return null;

          const author = item.user?.name ?? 'Unknown';
          const authorUrl = item.user?.links?.html ?? item.links?.html ?? '';

          return {
            id: item.id,
            thumbUrl,
            fullUrl,
            author,
            authorUrl,
          } satisfies UnsplashPhoto;
        })
        .filter((item): item is UnsplashPhoto => item !== null);

      if (requestId !== unsplashSearchRequestIdRef.current) {
        return;
      }

      const nextResults = append
        ? (() => {
            const seenIds = new Set(unsplashResults.map(item => item.id));
            const appendedItems = items.filter(item => !seenIds.has(item.id));
            return [...unsplashResults, ...appendedItems];
          })()
        : items;
      const nextHasMore =
        typeof payload.total_pages === 'number' ? page < payload.total_pages : items.length >= perPage;

      setUnsplashResults(nextResults);
      setUnsplashPage(page);
      setLastUnsplashQuery(query);
      setHasMoreUnsplashResults(nextHasMore);

      if (shouldCacheDefaultResults) {
        writeStoredDefaultGallerySearch(cacheKey, {
          query,
          page,
          hasMore: nextHasMore,
          results: nextResults,
        });
      }
    } catch (error) {
      if (requestId !== unsplashSearchRequestIdRef.current) {
        return;
      }
      const message = error instanceof Error ? error.message : 'Gallery search failed';
      setErrorMessage(message);
      if (!append && unsplashResults.length === 0) {
        setUnsplashResults([]);
        setHasMoreUnsplashResults(false);
      }
    } finally {
      if (append) {
        setIsUnsplashLoadingMore(false);
      } else {
        setIsUnsplashLoading(false);
      }
    }
  }

  useEffect(() => {
    if (activeTab !== 'unsplash' || !shouldShowSourceChooser) {
      return;
    }

    const query = unsplashQuery.trim();
    const shouldUseDefaultQuery = query.length === 0;
    const effectiveQuery = shouldUseDefaultQuery ? DEFAULT_GALLERY_QUERY : query;
    const cachedDefaultSearch = shouldUseDefaultQuery ? readStoredDefaultGallerySearch(cacheKey) : null;
    const shouldRefreshDefaultSearch = shouldUseDefaultQuery && !isStoredDefaultGallerySearchFresh(cachedDefaultSearch);

    if (shouldUseDefaultQuery) {
      if (cachedDefaultSearch) {
        setUnsplashResults(current =>
          areUnsplashResultsEqual(current, cachedDefaultSearch.results) ? current : cachedDefaultSearch.results,
        );
        setUnsplashPage(current => (current === cachedDefaultSearch.page ? current : cachedDefaultSearch.page));
        setHasMoreUnsplashResults(current =>
          current === cachedDefaultSearch.hasMore ? current : cachedDefaultSearch.hasMore,
        );
        setLastUnsplashQuery(current => (current === cachedDefaultSearch.query ? current : cachedDefaultSearch.query));
        setErrorMessage('');
      } else {
        setUnsplashResults(current => (current.length === 0 ? current : []));
        setUnsplashPage(current => (current === 1 ? current : 1));
        setHasMoreUnsplashResults(current => (current ? false : current));
        setLastUnsplashQuery(current => (current === '' ? current : ''));
      }

      if (cachedDefaultSearch && !shouldRefreshDefaultSearch) {
        return;
      }
    }

    if (effectiveQuery === lastUnsplashQuery && unsplashResults.length > 0 && !shouldRefreshDefaultSearch) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      void runUnsplashSearch({
        query: effectiveQuery,
        cacheDefaultResults: shouldUseDefaultQuery,
      });
    }, 300);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [activeTab, cacheKey, lastUnsplashQuery, shouldShowSourceChooser, unsplashQuery, unsplashResults.length]);

  async function handlePickUnsplash(photo: UnsplashPhoto): Promise<void> {
    if (visibleGallerySourceUrl === normalizeAssetUrl(photo.fullUrl) || selectedUnsplashPhotoId) {
      return;
    }

    setSelectedUnsplashPhotoId(photo.id);

    try {
      const dataUrl = await loadRemoteImageAsDataUrl(photo.fullUrl);
      const shouldKeepChooserOpen = isSourceChooserOpen;
      const nextWorkingImage = await applyWorkingImage(
        dataUrl,
        'unsplash',
        {
          author: photo.author,
          authorUrl: photo.authorUrl,
          sourceUrl: photo.fullUrl,
        },
        {
          keepSourceChooserOpen: shouldKeepChooserOpen,
        },
      );
      if (isSourceChooserOpen) {
        setPendingAutoApply({ dataUrl: nextWorkingImage.dataUrl, closeHost: false });
      } else {
        window.requestAnimationFrame(() => {
          workspaceRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      }
    } catch (error) {
      setSelectedUnsplashPhotoId(current => (current === photo.id ? null : current));
      const message = error instanceof Error ? error.message : 'Failed to pick Unsplash image';
      setErrorMessage(message);
    }
  }

  async function handleSaveToCache(options?: { closeHost?: boolean }): Promise<boolean> {
    if (!workingImage) {
      setErrorMessage('No image prepared yet.');
      return false;
    }

    let saved = false;
    try {
      setIsSavingToCache(true);
      const encodingMode = getWebpEncodingMode(workingImage.source);
      let variants: ExportedVariant[];

      try {
        variants = await exportProcessedVariantsInWorker({
          sourceDataUrl: workingImage.dataUrl,
          crop,
          targetWidth,
          targetHeight,
          scales: [...RESPONSIVE_VARIANT_SCALES],
          includeEditable: true,
          encodingMode,
        });
      } catch (workerError) {
        console.warn('ImageUploader worker export fallback', workerError);

        const sourceImage = await getWorkingImageElement(workingImage);
        const editableVariant = await generateEditableVariant(workingImage, sourceImage);
        const outputVariants = await Promise.all(
          RESPONSIVE_VARIANT_SCALES.map(async scale => {
            const width = targetWidth * scale;
            const height = targetHeight * scale;

            return generateProcessedVariant(workingImage, crop, width, height, sourceImage, scale, encodingMode);
          }),
        );
        variants = [editableVariant, ...outputVariants];
      }

      const primaryVariant = variants.find(variant => variant.descriptor === '1x') ?? variants[0];
      if (!primaryVariant) {
        setErrorMessage('No image variants were generated');
        return false;
      }

      const previewTargetSize = getPreviewTargetSize(targetWidth, targetHeight);
      const previewBlob = previewAsset?.blob ?? primaryVariant.blob;
      const previewBytes = previewAsset?.bytes ?? primaryVariant.bytes;
      const previewMimeType = previewAsset?.mimeType ?? primaryVariant.mimeType;
      const nextId = workingImage.cacheAssetId ?? createId();
      const existingCacheRecord = workingImage.cacheAssetId
        ? await getImageUploaderCacheRecord(cacheKey, workingImage.cacheAssetId)
        : null;
      const createdAt = existingCacheRecord?.createdAt ?? new Date().toISOString();
      const lastAccessedAt = new Date().toISOString();
      const serializedVariants = variants.map(serializeVariantSummary);
      const totalBytes = previewBytes + variants.reduce((sum, variant) => sum + variant.bytes, 0);
      const edit: ImageEditState = {
        crop: { ...crop },
        targetWidth,
        targetHeight,
      };

      const assetRecord: ImageUploaderCacheRecordInput = {
        cacheKey,
        id: nextId,
        source: workingImage.source,
        width: primaryVariant.width,
        height: primaryVariant.height,
        bytes: primaryVariant.bytes,
        mimeType: primaryVariant.mimeType,
        createdAt,
        lastAccessedAt,
        totalBytes,
        edit,
        preview: {
          blob: previewBlob,
          width: previewTargetSize.width,
          height: previewTargetSize.height,
          bytes: previewBytes,
          mimeType: previewMimeType,
        },
        variants,
        ...(workingImage.attribution ? { attribution: workingImage.attribution } : {}),
      };

      const manifests = await saveImageUploaderCacheRecord(assetRecord, {
        maxItems: maxCachedItems,
        maxTotalBytes: DEFAULT_MAX_CACHE_TOTAL_BYTES,
      });

      replaceCacheItems(manifests.map(buildCacheSummary));
      setWorkingImage(current => (current ? { ...current, cacheAssetId: nextId } : current));

      // Frontend generates the browser-side outputs here: editable source + 1x and 2x WebP variants.
      const eventAsset = {
        source: workingImage.source,
        width: primaryVariant.width,
        height: primaryVariant.height,
        bytes: primaryVariant.bytes,
        mimeType: primaryVariant.mimeType,
        variants: serializedVariants,
        createdAt,
        edit,
        ...(workingImage.attribution ? { attribution: workingImage.attribution } : {}),
      };

      const targetEl = target ? document.getElementById(target) : null;
      if (!targetEl || !pushEventTo) {
        throw new Error('Image uploader target is not available');
      }

      if (!uploadUrl) {
        throw new Error('Image uploader upload URL is not configured');
      }

      const persistedAsset = await uploadPreparedAssetToBackend(uploadUrl, variants, eventAsset);
      const refreshedManifests = await updateImageUploaderCachePersistedAsset(
        cacheKey,
        nextId,
        toPersistedAssetRef(persistedAsset),
      );
      replaceCacheItems(refreshedManifests.map(buildCacheSummary));
      const persistedIdentity = getPersistedAssetIdentity(persistedAsset);
      hydratedPersistedAssetIdentityRef.current = persistedIdentity;
      setWorkingImage(current =>
        current
          ? {
              ...current,
              ...(persistedIdentity ? { persistedIdentity } : {}),
            }
          : current,
      );
      if (persistedIdentity) {
        setAppliedEditorState({
          imageIdentity: persistedIdentity,
          crop: { ...crop },
          targetWidth,
          targetHeight,
        });
      }
      pushEventTo(targetEl, eventName, { asset: withDisplayFields(persistedAsset) });
      if (closeModalId && options?.closeHost !== false) {
        window.requestAnimationFrame(() => {
          closeHostModal(closeModalId);
        });
      }

      setErrorMessage('');
      saved = true;
      return true;
    } catch (error) {
      setSelectedUnsplashPhotoId(null);
      setPendingRecentSelectionId(null);
      const message = error instanceof Error ? error.message : 'Failed to save cache item';
      setErrorMessage(message);
      return false;
    } finally {
      settleCanvasPreview(saved);
      setIsSavingToCache(false);
      setApplyingColorValue(null);
      setApplyingCacheItemId(null);
    }
  }

  async function handleUseCacheItem(asset: CachedAssetSummary): Promise<void> {
    if (selectedRecentCacheItemId === asset.id) {
      return;
    }

    setApplyingCacheItemId(asset.id);
    setPendingRecentSelectionId(asset.id);

    try {
      const record = await getImageUploaderCacheRecord(cacheKey, asset.id);
      if (!record) {
        const manifests = await listImageUploaderCacheManifests(cacheKey);
        replaceCacheItems(manifests.map(buildCacheSummary));
        setErrorMessage('Cached item is no longer available');
        setPendingRecentSelectionId(null);
        setApplyingCacheItemId(null);
        return;
      }

      const editableVariant = record.variants.find(variant => variant.descriptor === 'editable');
      const primaryVariant = record.variants.find(variant => variant.descriptor === '1x') ?? record.variants[0];
      const usesEditableVariant = !isLegacyEditableVariant(editableVariant, primaryVariant);
      const editorVariant = usesEditableVariant ? (editableVariant ?? primaryVariant) : primaryVariant;
      const edit = record.edit ?? {
        crop: createConfiguredDefaultCropRect(
          editorVariant?.width ?? primaryVariant?.width ?? defaultTargetWidth,
          editorVariant?.height ?? primaryVariant?.height ?? defaultTargetHeight,
          primaryVariant?.width ?? defaultTargetWidth,
          primaryVariant?.height ?? defaultTargetHeight,
        ),
        targetWidth: primaryVariant?.width ?? defaultTargetWidth,
        targetHeight: primaryVariant?.height ?? defaultTargetHeight,
      };
      const nextCrop = edit.crop;

      if (!primaryVariant || !editorVariant) {
        setErrorMessage('Cached item has no usable variant');
        setPendingRecentSelectionId(null);
        setApplyingCacheItemId(null);
        return;
      }

      revokeWorkingImageObjectUrl();
      workingImageElementRef.current = null;

      const sourceUrl = URL.createObjectURL(editorVariant.blob);
      workingImageObjectUrlRef.current = sourceUrl;

      replacePreview({
        src: URL.createObjectURL(record.preview.blob),
        blob: record.preview.blob,
        bytes: record.preview.bytes,
        mimeType: record.preview.mimeType,
      });

      setWorkingImage({
        dataUrl: sourceUrl,
        source: asset.source,
        naturalWidth: editorVariant.width,
        naturalHeight: editorVariant.height,
        cacheAssetId: record.id,
        ...(getPersistedAssetRefIdentity(record.persisted)
          ? { persistedIdentity: getPersistedAssetRefIdentity(record.persisted)! }
          : {}),
        ...(asset.attribution ? { attribution: asset.attribution } : {}),
      });
      const hydratedCrop = usesEditableVariant ? nextCrop : { x: 0, y: 0, width: 100, height: 100 };
      const hydratedTargetWidth = usesEditableVariant
        ? isFixedSize
          ? defaultTargetWidth
          : edit.targetWidth
        : isFixedSize
          ? defaultTargetWidth
          : primaryVariant.width;
      const hydratedTargetHeight = usesEditableVariant
        ? isFixedSize
          ? defaultTargetHeight
          : edit.targetHeight
        : isFixedSize
          ? defaultTargetHeight
          : primaryVariant.height;
      setTargetWidth(hydratedTargetWidth);
      setTargetHeight(hydratedTargetHeight);
      setCrop(hydratedCrop);
      setAspectLocked(true);
      setActiveTab(asset.source);
      setIsSourceChooserOpen(true);
      if (asset.source === 'color') {
        const hydratedColor = await extractHexColorFromImageSource(sourceUrl);
        if (hydratedColor) {
          setSelectedColor(normalizeHydratedSelectedColor(hydratedColor));
        }
      }
      const nextImageIdentity = getPersistedAssetRefIdentity(record.persisted) ?? record.id;
      setAppliedEditorState({
        imageIdentity: nextImageIdentity,
        crop: hydratedCrop,
        targetWidth: hydratedTargetWidth,
        targetHeight: hydratedTargetHeight,
      });
      if (isSourceChooserOpen) {
        const targetEl = target ? document.getElementById(target) : null;
        const persistedIdentity = getPersistedAssetRefIdentity(record.persisted);

        if (record.persisted && persistedIdentity && targetEl && pushEventTo) {
          const persistedAsset = buildPersistedAssetFromCacheRecord(record, edit, record.persisted);
          hydratedPersistedAssetIdentityRef.current = persistedIdentity;
          pushEventTo(targetEl, eventName, { asset: withDisplayFields(persistedAsset) });
          if (closeModalId && asset.source !== 'color') {
            window.requestAnimationFrame(() => {
              closeHostModal(closeModalId);
            });
          }
          setApplyingCacheItemId(null);
        } else {
          setPendingAutoApply({ dataUrl: sourceUrl, closeHost: asset.source !== 'color' });
        }
      } else {
        setPendingRecentSelectionId(null);
        setApplyingCacheItemId(null);
      }
      setErrorMessage('');

      await touchImageUploaderCacheRecord(cacheKey, asset.id);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load cached item';
      setErrorMessage(message);
      setPendingRecentSelectionId(null);
      setApplyingCacheItemId(null);
    }
  }

  function handleOpenSourceChooser() {
    window.setTimeout(() => {
      if (customColorApplyTimeoutRef.current !== null) {
        window.clearTimeout(customColorApplyTimeoutRef.current);
        customColorApplyTimeoutRef.current = null;
      }

      setIsSourceChooserOpen(true);
      setIsRecentTabActive(false);
      setActiveTab(defaultSourceTab);
      setErrorMessage('');
    }, 0);
  }

  function handleCloseSourceChooser() {
    window.setTimeout(() => {
      if (customColorApplyTimeoutRef.current !== null) {
        window.clearTimeout(customColorApplyTimeoutRef.current);
        customColorApplyTimeoutRef.current = null;
      }

      setIsSourceChooserOpen(false);
      setErrorMessage('');
    }, 0);
  }

  useEffect(() => {
    saveToCacheRef.current = handleSaveToCache;
  });

  const shouldAutoSaveCrop =
    showCropEditor &&
    !shouldShowSourceChooser &&
    isCropDirty &&
    !cropDragState &&
    !isGeneratingPreview &&
    !isSavingToCache &&
    !pendingAutoApply;

  useEffect(() => {
    if (!shouldAutoSaveCrop || !workingImage) return;

    const failed = failedCropAutoSaveRef.current;
    if (
      failed &&
      failed.dataUrl === workingImage.dataUrl &&
      failed.targetWidth === targetWidth &&
      failed.targetHeight === targetHeight &&
      areCropRectsEqual(failed.crop, crop)
    ) {
      return;
    }

    const attempt = { dataUrl: workingImage.dataUrl, crop, targetWidth, targetHeight };
    const timeoutId = window.setTimeout(() => {
      void saveToCacheRef.current?.({ closeHost: false }).then(saved => {
        failedCropAutoSaveRef.current = saved ? null : attempt;
      });
    }, CROP_AUTO_SAVE_DELAY_MS);

    return () => window.clearTimeout(timeoutId);
  }, [shouldAutoSaveCrop, workingImage, crop, targetWidth, targetHeight]);

  // Uploading takes a few seconds, so the canvas shows the local crop preview until LiveView patches in the saved image.
  useEffect(() => {
    if (!appliedImageSelector || !previewAsset || cropDragState) return;
    if (!showCropEditor || shouldShowSourceChooser || !isCropDirty) return;

    const image = document.querySelector<HTMLImageElement>(appliedImageSelector);
    if (!image) return;

    canvasSrcBeforePreviewRef.current ??= image.getAttribute('src');
    image.src = previewAsset.src;
  }, [appliedImageSelector, previewAsset, cropDragState, showCropEditor, shouldShowSourceChooser, isCropDirty]);

  function settleCanvasPreview(saved: boolean) {
    const originalSrc = canvasSrcBeforePreviewRef.current;
    canvasSrcBeforePreviewRef.current = null;
    if (saved || originalSrc === null || !appliedImageSelector) return;

    document.querySelector<HTMLImageElement>(appliedImageSelector)?.setAttribute('src', originalSrc);
  }

  useEffect(() => {
    if (!pendingAutoApply || !workingImage) return;
    if (workingImage.dataUrl !== pendingAutoApply.dataUrl) return;
    if (isSavingToCache) return;

    const closeHost = pendingAutoApply.closeHost;
    setPendingAutoApply(null);
    void handleSaveToCache({ closeHost });
  }, [handleSaveToCache, isSavingToCache, pendingAutoApply, workingImage]);

  function startCropDrag(event: ReactPointerEvent<HTMLDivElement>, mode: CropDragMode) {
    event.preventDefault();
    event.stopPropagation();

    setCropDragState({
      mode,
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      initialCrop: crop,
    });
  }

  return (
    <div ref={uploaderRootRef} className='text-base-700 overflow-hidden rounded-xl text-sm'>
      <div className='mx-auto w-full max-w-[440px] px-4'>
        <div className={shouldShowSourceChooser ? 'flex max-h-[68vh] min-h-0 flex-col' : ''}>
          {showChooserTabBar ? (
            <div className='sticky top-0 z-10 bg-white pb-4 pt-2'>
              <div className='border-base-200/80 flex flex-wrap items-center gap-x-5 gap-y-3 border-b'>
                {chooserTabs.map(tab => {
                  const selected =
                    tab === 'recent'
                      ? shouldShowSourceChooser && isRecentTabActive
                      : tab === 'edit'
                        ? isEditTabActive
                        : !isRecentTabActive && !isEditTabActive && activeTab === tab;

                  return (
                    <button
                      key={tab}
                      type='button'
                      onClick={() => {
                        if (tab === 'recent') {
                          setIsRecentTabActive(true);
                          setIsSourceChooserOpen(true);
                          setErrorMessage('');
                          return;
                        }

                        if (tab === 'edit') {
                          setIsRecentTabActive(false);
                          handleCloseSourceChooser();
                          return;
                        }

                        if (activeTab === tab && shouldShowSourceChooser && !isRecentTabActive) {
                          return;
                        }

                        const sourceTab = tab as SourceType;
                        setIsRecentTabActive(false);
                        setIsSourceChooserOpen(true);
                        setActiveTab(sourceTab);
                        setErrorMessage('');
                      }}
                      className={`-mb-px inline-flex items-center whitespace-nowrap border-b-2 pb-3 pt-1 text-sm font-semibold transition ${
                        selected
                          ? 'border-base-700 text-base-700'
                          : 'text-base-500 hover:text-base-700 border-transparent'
                      } ${
                        tab === firstChooserTabAfterEdit
                          ? 'before:bg-base-300/90 relative before:pointer-events-none before:absolute before:-left-2.5 before:top-[46%] before:h-4 before:w-px before:-translate-y-1/2'
                          : ''
                      }`}
                    >
                      {tab === 'recent' ? 'Recent' : tab === 'edit' ? 'Edit' : tabLabel[tab as SourceType]}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : hasSource && !showCropEditor ? (
            <div className='pt-3'>
              <button
                type='button'
                onClick={handleOpenSourceChooser}
                className={`${FLUXON_OUTLINE_BUTTON_SM_CLASS} w-full`}
              >
                Change image
              </button>
            </div>
          ) : null}

          <div
            className={`pb-4 ${shouldUseScrollableChooser ? 'min-h-0 flex-1 overflow-y-auto overscroll-contain' : ''}`}
          >
            {errorMessage && (
              <div className='mb-4'>
                <div className='rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700'>
                  {errorMessage}
                </div>
              </div>
            )}

            {shouldShowSourcePanel && activeTab === 'upload' && (
              <div className='space-y-3'>
                <label
                  htmlFor={fileInputId}
                  onDragOver={handleUploadDragOver}
                  onDragEnter={handleUploadDragOver}
                  onDragLeave={handleUploadDragLeave}
                  onDrop={handleUploadDrop}
                  className={`group cursor-pointer overflow-hidden rounded-2xl border transition ${
                    isUploadDragging
                      ? 'border-base-400 bg-base-50 border-dashed'
                      : showUploadSourcePreviewCard
                        ? 'bg-base-25 hover:border-base-300 hover:bg-base-50 border-transparent hover:border-dashed'
                        : 'border-base-200/80 bg-base-25 hover:border-base-300 hover:bg-base-50 border-dashed'
                  } ${showUploadSourcePreviewCard ? 'block' : 'flex min-h-[168px] flex-col items-center justify-center px-4 text-center'}`}
                >
                  {showUploadSourcePreviewCard && sourcePreviewSrc ? (
                    <div className='relative aspect-[4/3] overflow-hidden'>
                      <img src={sourcePreviewSrc} alt='Uploaded preview' className='h-full w-full object-cover' />
                      <div
                        className={`pointer-events-none absolute inset-0 flex items-center justify-center bg-black/45 px-4 text-center text-white transition ${
                          isUploadDragging ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                        }`}
                      >
                        <span className='text-sm font-semibold'>Choose a file or drag here</span>
                      </div>
                    </div>
                  ) : (
                    <>
                      <Icon name='hero-arrow-up-tray' className='text-base-400 mb-3 text-4xl' />
                      <div className='text-base-700 text-lg font-semibold'>Choose a file or drag here</div>
                    </>
                  )}
                  <input
                    id={fileInputId}
                    type='file'
                    accept='image/*'
                    aria-label='Select image from your device'
                    className='sr-only'
                    onChange={event => {
                      const file = event.target.files?.[0] ?? null;
                      void handleUploadFile(file);
                      event.currentTarget.value = '';
                    }}
                  />
                </label>
              </div>
            )}

            {shouldShowSourcePanel && activeTab === 'link' && (
              <div className='space-y-3'>
                <div className='border-base-200 flex min-w-0 items-stretch overflow-hidden rounded-lg border bg-white transition focus-within:border-indigo-500 focus-within:ring-1 focus-within:ring-inset focus-within:ring-indigo-500 focus-within:[&>button]:border-indigo-500'>
                  <input
                    type='url'
                    value={linkInput}
                    onChange={event => setLinkInput(event.target.value)}
                    onKeyDown={event => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        void handleLoadFromLink();
                      }
                    }}
                    placeholder='https://example.com/image.jpg'
                    className='text-base-700 placeholder:text-base-500 min-w-0 flex-1 border-0 bg-transparent px-4 py-2 text-sm outline-none'
                  />
                  <button
                    type='button'
                    onClick={() => void handleLoadFromLink()}
                    className='border-base-200 text-base-500 hover:text-base-700 hover:bg-base-50 focus:outline-hidden focus-visible:outline-hidden flex h-auto w-14 shrink-0 items-center justify-center border-l bg-white transition focus-visible:bg-indigo-50 focus-visible:text-indigo-600'
                    aria-label='Load image from link'
                    title='Load image'
                  >
                    <Icon name='hero-arrow-up-tray' className='h-4 w-4' />
                  </button>
                </div>

                {showLinkSourcePreviewCard && sourcePreviewSrc && (
                  <div>
                    <div className='border-base-200 block w-full overflow-hidden rounded-xl border bg-white'>
                      <div className='relative aspect-[4/3] overflow-hidden'>
                        <img src={sourcePreviewSrc} alt='Linked image preview' className='h-full w-full object-cover' />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {shouldShowSourcePanel && activeTab === 'unsplash' && (
              <div className='space-y-4'>
                <div className='relative min-w-0'>
                  <Icon
                    name='hero-magnifying-glass'
                    className='text-base-400 absolute left-3 top-1/2 -translate-y-1/2 text-base'
                  />
                  <input
                    ref={gallerySearchInputRef}
                    type='text'
                    value={unsplashQuery}
                    onChange={event => setUnsplashQuery(event.target.value)}
                    onKeyDown={event => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        void runUnsplashSearch();
                      }
                    }}
                    placeholder='Search gallery images'
                    className='border-base-200 text-base-700 focus:outline-hidden w-full rounded-lg border bg-white py-2 pl-10 pr-10 text-sm transition focus:border-indigo-500 focus:ring-1 focus:ring-inset focus:ring-indigo-500'
                  />
                  {(isUnsplashLoading || isUnsplashLoadingMore) && (
                    <span className='text-base-400 absolute right-3 top-1/2 -translate-y-1/2'>
                      <Icon name='hero-arrow-path' className='h-4 w-4 animate-spin' />
                    </span>
                  )}
                </div>

                {!unsplashAccessKey && (
                  <div className='border-b border-amber-200 bg-amber-50 px-5 py-3 text-xs text-amber-700'>
                    Gallery key missing. Add <code>UNSPLASH_ACCESS_KEY</code> and restart the Phoenix server.
                  </div>
                )}

                {unsplashResults.length > 0 ? (
                  <div>
                    <div className='grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(140px,1fr))]'>
                      {unsplashResults.map(photo => {
                        const selected = visibleGallerySourceUrl === normalizeAssetUrl(photo.fullUrl);
                        const applying = selectedUnsplashPhotoId === photo.id && !selected;
                        const highlighted = selected || applying;
                        const imageLoaded = Boolean(loadedGalleryImageIds[photo.id]);

                        return (
                          <button
                            key={photo.id}
                            type='button'
                            onClick={() => void handlePickUnsplash(photo)}
                            disabled={isSavingToCache || Boolean(selectedUnsplashPhotoId)}
                            aria-label={`Use gallery image by ${photo.author}`}
                            className={`relative overflow-hidden rounded-lg border text-left transition ${
                              highlighted
                                ? 'border-indigo-500 ring-2 ring-indigo-200'
                                : 'border-base-200 hover:border-base-300 hover:shadow-xs'
                            } ${isSavingToCache || selectedUnsplashPhotoId ? 'disabled:cursor-wait disabled:opacity-70' : ''}`}
                            aria-pressed={selected}
                            aria-busy={applying}
                          >
                            {!imageLoaded && <div className='bg-base-100 absolute inset-0 animate-pulse' />}
                            <img
                              src={photo.thumbUrl}
                              alt=''
                              loading='lazy'
                              onLoad={() =>
                                setLoadedGalleryImageIds(current =>
                                  current[photo.id] ? current : { ...current, [photo.id]: true },
                                )
                              }
                              className={`h-28 w-full object-cover transition-opacity duration-150 ${
                                imageLoaded ? 'opacity-100' : 'opacity-0'
                              }`}
                            />
                            {highlighted ? (
                              <>
                                <span className='absolute inset-1 rounded-md ring-2 ring-white/90' />
                                <span className='text-base-900 absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-full bg-white shadow-sm'>
                                  <Icon
                                    name={applying ? 'hero-arrow-path' : 'hero-check'}
                                    className={`h-4 w-4 ${applying ? 'animate-spin' : ''}`}
                                  />
                                </span>
                                {applying ? (
                                  <div className='bg-base-900/25 absolute inset-0 flex items-end justify-center pb-3 text-white'>
                                    <span className='text-xs font-medium'>Applying...</span>
                                  </div>
                                ) : null}
                              </>
                            ) : null}
                          </button>
                        );
                      })}
                    </div>

                    {hasMoreUnsplashResults && activeGalleryQuery === lastUnsplashQuery && (
                      <div className='mt-4 flex justify-center'>
                        <button
                          type='button'
                          onClick={() =>
                            void runUnsplashSearch({
                              page: unsplashPage + 1,
                              append: true,
                              query: activeGalleryQuery,
                              cacheDefaultResults: activeGalleryQuery === DEFAULT_GALLERY_QUERY,
                            })
                          }
                          disabled={isUnsplashLoadingMore || isUnsplashLoading}
                          className={`${FLUXON_OUTLINE_BUTTON_SM_CLASS} disabled:cursor-not-allowed disabled:opacity-50`}
                        >
                          {isUnsplashLoadingMore ? 'Loading...' : 'Load more'}
                        </button>
                      </div>
                    )}
                  </div>
                ) : isUnsplashLoading ? (
                  <div className='bg-base-25 text-base-500 flex min-h-[220px] flex-col items-center justify-center rounded-xl px-4 text-center'>
                    <Icon name='hero-arrow-path' className='mb-3 h-5 w-5 animate-spin' />
                    <span className='text-sm font-medium'>Loading gallery images...</span>
                  </div>
                ) : unsplashQuery.trim() ? (
                  <div className='bg-base-25 text-base-500 flex min-h-[220px] items-center justify-center rounded-xl px-4 text-center text-sm'>
                    No gallery images found for this search.
                  </div>
                ) : (
                  <div className='bg-base-25 text-base-500 flex min-h-[220px] items-center justify-center rounded-xl px-4 text-center text-sm'>
                    No gallery images available right now.
                  </div>
                )}
              </div>
            )}

            {shouldShowSourcePanel && activeTab === 'color' && (
              <div>
                <div className='flex justify-center'>
                  <div className='grid w-full grid-cols-3 gap-3'>
                    {colorOptions.map(color => {
                      const selected = normalizedSelectedColor === color.toLowerCase();
                      const applying = applyingColorValue?.toLowerCase() === color.toLowerCase();

                      return (
                        <button
                          key={color}
                          type='button'
                          onClick={() => void handleSelectColor(color)}
                          disabled={Boolean(applyingColorValue)}
                          className={`relative h-16 w-full overflow-hidden rounded-xl border transition ${
                            selected
                              ? 'border-indigo-500 shadow-sm ring-2 ring-indigo-200'
                              : 'border-base-200 hover:border-base-300'
                          } ${applyingColorValue ? 'disabled:cursor-wait disabled:opacity-70' : ''}`}
                          style={{ backgroundColor: color }}
                          aria-label={`Color ${color}`}
                          aria-pressed={selected}
                          aria-busy={applying}
                        >
                          {selected ? (
                            <>
                              <span className='pointer-events-none absolute inset-1 rounded-[10px] ring-2 ring-white/90' />
                              <span className='text-base-900 pointer-events-none absolute right-1.5 top-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-white shadow-sm'>
                                <Icon
                                  name={applying ? 'hero-arrow-path' : 'hero-check'}
                                  className={`h-4 w-4 ${applying ? 'animate-spin' : ''}`}
                                />
                              </span>
                              {applying ? (
                                <div className='bg-base-900/25 absolute inset-0 flex items-end justify-center pb-2 text-white'>
                                  <span className='text-[11px] font-medium'>Applying...</span>
                                </div>
                              ) : null}
                            </>
                          ) : null}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className='flex justify-center pt-4'>
                  <button
                    type='button'
                    onClick={() => customColorInputRef.current?.click()}
                    disabled={Boolean(applyingColorValue)}
                    className='border-base-200 text-base-900 shadow-xs hover:bg-base-50 inline-flex items-center justify-center rounded-md border border-dashed bg-white px-4 py-2 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-70'
                  >
                    Custom
                  </button>
                  <input
                    ref={customColorInputRef}
                    type='color'
                    value={selectedColor}
                    onChange={event => handleCustomColorPicked(event.currentTarget.value)}
                    className='sr-only'
                    tabIndex={-1}
                    aria-hidden='true'
                  />
                </div>
              </div>
            )}

            {showEditorWorkspace && (
              <div ref={workspaceRef} className='space-y-3'>
                {hasSource && !showCropEditor && (
                  <div className='pb-3'>
                    <div className='space-y-4'>
                      <div className='flex justify-center'>
                        <div className='w-full max-w-[160px] overflow-hidden rounded-xl sm:max-w-[180px]'>
                          {previewAsset ? (
                            <img
                              src={previewAsset.src}
                              alt='Preview'
                              className='w-full rounded-md object-cover'
                              style={{ aspectRatio: `${targetWidth} / ${targetHeight}` }}
                            />
                          ) : (
                            <div
                              className='text-base-500 flex items-center justify-center rounded-md border border-dashed text-xs'
                              style={{ aspectRatio: `${targetWidth} / ${targetHeight}` }}
                            >
                              {isGeneratingPreview ? 'Rendering...' : 'Preview'}
                            </div>
                          )}
                        </div>
                      </div>

                      {shouldShowApplyButton && (
                        <div>
                          <button
                            type='button'
                            disabled={!canGenerate || isSavingToCache}
                            onClick={() => void handleSaveToCache()}
                            className={`${FLUXON_SOLID_BUTTON_CLASS} w-full disabled:cursor-not-allowed disabled:opacity-50`}
                          >
                            {isSavingToCache ? savingLabel : 'Save changes'}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {showCropEditor && (
                  <div>
                    {workingImage && (
                      <>
                        <div
                          ref={sourceImageRef}
                          className='border-base-200 relative select-none overflow-hidden rounded-xl border bg-white'
                        >
                          <img src={workingImage.dataUrl} alt='Selected source' className='w-full' draggable={false} />
                          <div
                            className='absolute shadow-[0_0_0_9999px_rgba(24,24,27,0.42)]'
                            style={{
                              left: `${crop.x}%`,
                              top: `${crop.y}%`,
                              width: `${crop.width}%`,
                              height: `${crop.height}%`,
                            }}
                          >
                            <div
                              aria-hidden='true'
                              className='pointer-events-none absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.35)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.35)_1px,transparent_1px)] bg-[length:33.333%_100%,100%_33.333%]'
                            />
                            <div
                              role='button'
                              aria-label='Move crop area'
                              className='absolute inset-0 cursor-move touch-none'
                              onPointerDown={event => startCropDrag(event, 'move')}
                            />
                            <div
                              role='button'
                              aria-label='Resize crop area from top left'
                              className='bg-base-700 absolute left-0 top-0 h-4 w-4 cursor-nwse-resize touch-none rounded-full border-2 border-white shadow-sm'
                              onPointerDown={event => startCropDrag(event, 'resize-top-left')}
                            />
                            <div
                              role='button'
                              aria-label='Resize crop area from top right'
                              className='bg-base-700 absolute right-0 top-0 h-4 w-4 cursor-nesw-resize touch-none rounded-full border-2 border-white shadow-sm'
                              onPointerDown={event => startCropDrag(event, 'resize-top-right')}
                            />
                            <div
                              role='button'
                              aria-label='Resize crop area from bottom left'
                              className='bg-base-700 absolute bottom-0 left-0 h-4 w-4 cursor-nesw-resize touch-none rounded-full border-2 border-white shadow-sm'
                              onPointerDown={event => startCropDrag(event, 'resize-bottom-left')}
                            />
                            <div
                              role='button'
                              aria-label='Resize crop area from bottom right'
                              className='bg-base-700 absolute bottom-0 right-0 h-4 w-4 cursor-nwse-resize touch-none rounded-full border-2 border-white shadow-sm'
                              onPointerDown={event => startCropDrag(event, 'resize-bottom-right')}
                            />
                          </div>
                        </div>

                        <div className='mt-4 space-y-4 empty:hidden'>
                          {showDisplayOptions && hasPersistedAsset && (
                            <>
                              <DisplayOptionSelect
                                label='Size'
                                value={effectiveDisplay.size}
                                options={DISPLAY_SIZE_OPTIONS}
                                onChange={size => pushDisplayFields({ ...effectiveDisplay, size })}
                              />
                              <SegmentedControl
                                label='Align'
                                value={effectiveDisplay.align}
                                options={DISPLAY_ALIGN_OPTIONS}
                                disabled={effectiveDisplay.size === 'full'}
                                disabledHint='Not used at full width'
                                onChange={align => pushDisplayFields({ ...effectiveDisplay, align })}
                              />
                            </>
                          )}

                          {showDisplayOptions && !isFixedSize && (
                            <SegmentedControl
                              label='Aspect ratio'
                              value={aspectLocked ? 'locked' : 'unlocked'}
                              options={ASPECT_LOCK_OPTIONS}
                              onChange={mode => setAspectLocked(mode === 'locked')}
                            />
                          )}

                          {!isFixedSize && (
                            <>
                              {showPresetSelector && (
                                <div>
                                  <p className={OPTION_LABEL_CLASS}>Aspect</p>
                                  <div className='flex flex-wrap gap-2'>
                                    {outputPresets.map(preset => {
                                      const isActive = preset.original
                                        ? isOriginalAspectActive
                                        : targetWidth === preset.width && targetHeight === preset.height;

                                      return (
                                        <button
                                          key={preset.key}
                                          type='button'
                                          aria-pressed={isActive}
                                          onClick={() => applyOutputPreset(preset)}
                                          className={optionChipClass(isActive)}
                                        >
                                          {preset.label}
                                        </button>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}

                              {showSizeInputs && (
                                <div className='grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(140px,1fr))]'>
                                  <label className='text-base-500 text-xs'>
                                    Width
                                    <input
                                      type='number'
                                      min={1}
                                      value={targetWidth}
                                      onChange={event => setTargetWidth(Math.max(1, Number(event.target.value) || 1))}
                                      className='border-base-200 text-base-700 mt-1 w-full min-w-[132px] rounded-lg border px-3 py-2 text-sm outline-none'
                                    />
                                  </label>
                                  <label className='text-base-500 text-xs'>
                                    Height
                                    <input
                                      type='number'
                                      min={1}
                                      value={targetHeight}
                                      onChange={event => setTargetHeight(Math.max(1, Number(event.target.value) || 1))}
                                      className='border-base-200 text-base-700 mt-1 w-full min-w-[132px] rounded-lg border px-3 py-2 text-sm outline-none'
                                    />
                                  </label>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}

            {shouldShowRecentImages && (
              <div className='grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(140px,1fr))]'>
                {cacheItems.map(item => {
                  const selected = selectedRecentCacheItemId === item.id;
                  const applying = pendingRecentSelectionId === item.id && !selected;
                  const highlighted = selected || applying;

                  return (
                    <button
                      key={item.id}
                      type='button'
                      onClick={() => void handleUseCacheItem(item)}
                      disabled={Boolean(applyingCacheItemId) || Boolean(pendingRecentSelectionId) || isSavingToCache}
                      className={`relative overflow-hidden rounded-lg border bg-white text-left transition ${
                        highlighted
                          ? 'border-indigo-500 ring-2 ring-indigo-200'
                          : 'border-base-200 hover:border-base-300 hover:shadow-xs'
                      } ${
                        applyingCacheItemId || pendingRecentSelectionId || isSavingToCache
                          ? 'disabled:cursor-wait disabled:opacity-70'
                          : ''
                      }`}
                      aria-pressed={selected}
                      aria-busy={applying}
                    >
                      <img
                        src={item.previewUrl}
                        alt={`${tabLabel[item.source]} preview`}
                        className='h-28 w-full object-cover'
                      />
                      {highlighted ? (
                        <>
                          <span className='absolute inset-1 rounded-md ring-2 ring-white/90' />
                          <span className='text-base-900 absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-full bg-white shadow-sm'>
                            <Icon
                              name={applying ? 'hero-arrow-path' : 'hero-check'}
                              className={`h-4 w-4 ${applying ? 'animate-spin' : ''}`}
                            />
                          </span>
                          {applying ? (
                            <div className='bg-base-900/25 absolute inset-0 flex items-end justify-center pb-3 text-white'>
                              <span className='text-xs font-medium'>Applying...</span>
                            </div>
                          ) : null}
                        </>
                      ) : null}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
